import { html, useState, useMemo, useEffect } from '/vendor/htm-preact.module.js';
import { del, get, post, put } from '/api.js';
import { useRead } from '/read.js';
import { RED, YELLOW, applyShot, newFrame, shotsFor } from '/rules.js';
import { Icon } from '/icons.js';
import { RoleMarks } from '/rolemark.js';
import { ImageUpload, Picture } from '/imageupload.js';
import { venueLine } from '/venues.js';
import { LeagueCalendar } from '/calendar.js';
import { KeepPage } from '/shortcuts.js';
import { useQrSvg } from '/qr.js';

/**
 * The link a league invite QR carries — issue #89.
 *
 * NOT `/?invite=`, which is the ACCOUNT invite and is read only by the
 * sign-in screen. The two codes are different machinery on purpose:
 * `/api/leagues/join` "does not create anybody. They are already signed in,
 * which is the whole difference between the two codes" (cueforge/api.py), and
 * registration goes through `redeem_invite`, so a league code cannot make an
 * account. Pointing a league QR at the account path would hand somebody a
 * sign-in form their code cannot get them through.
 *
 * The code rides in the QUERY and the screen in the HASH. Both are needed:
 * the hash is what router.js reads to open the leagues screen, and a hash is
 * also the only part that survives offline (see router.js on why this app
 * does not use real paths), while `location.search` is where a value can sit
 * without the router having to learn about it.
 */
const LEAGUE_CODE_PARAM = 'league';

export function leagueInviteUrl(code) {
  return `${location.origin}/?${LEAGUE_CODE_PARAM}`
    + `=${encodeURIComponent(code)}#/leagues`;
}

/**
 * The leagues you are registered to.
 *
 * Built round being EMPTY, because that is what it is for nearly everybody:
 * the message and the code box are the page until somebody has joined one.
 * A league is not something you sign yourself up to — an administrator hands
 * out a code — so the empty state has to say where the code comes from
 * rather than leave somebody looking for a button that is not there.
 */
/**
 * One league's box on the Leagues page — GitHub issue #72.
 *
 * It was a name and a member count. Ben asked for the box to describe the
 * league: how many divisions, teams and players, when its season runs, and
 * a place on the right for its crest.
 *
 * EVERYTHING HERE IS DERIVED, from the league's own book — see
 * `_league_facts` in api.py. Nothing new is stored to say any of it, so
 * none of it can drift from the archive it describes.
 *
 * A league run HERE has no archive and therefore no facts, and its box is
 * the name and the member count it always was. That is why every line
 * below is conditional rather than a row of dashes: a box full of "—" says
 * the screen is broken, and this one is not.
 */
function LeagueCard({ league: l, onGo }) {
  const f = l.facts || {};
  // Left-justified, as asked. The crest sits opposite on its own, so this
  // is a two-column row rather than the `rowbetween` a one-line entry used.
  const bits = [
    f.divisions && `${f.divisions} ${f.divisions === 1 ? 'division' : 'divisions'}`,
    f.teams && `${f.teams} ${f.teams === 1 ? 'team' : 'teams'}`,
    f.players && `${f.players} ${f.players === 1 ? 'player' : 'players'}`,
    `${l.members} ${l.members === 1 ? 'member' : 'members'}`,
  ].filter(Boolean);

  return html`
    <button class="secondary card" style="width:100%;text-align:left;
                                          display:flex;gap:.75rem;
                                          align-items:flex-start"
            data-testid=${`league-item-${l.league_id}`}
            onClick=${() => onGo('league', l.league_id)}>
      <span style="flex:1;min-width:0">
        ${/* Slightly larger than everything under it, which is what #72
              asks for. A size rather than a heading tag: this is inside a
              button, and an <h3> in a button is a lie to a screen reader
              about what can be pressed. */''}
        <span data-testid=${`league-name-${l.league_id}`}
              style="font-size:1.05rem;font-weight:700;display:block">
          ${l.name}
        </span>
        ${/* The long name, under the short one, exactly as issue #72 draws
              it. Typed by the committee -- nothing derives "BDIPL stands
              for the Basingstoke and Deane Invitational Pool League" -- so
              it is simply absent until somebody has said it. */''}
        ${l.full_name && html`
          <span class="sub" data-testid=${`league-fullname-${l.league_id}`}
                style="display:block;text-transform:none">
            ${l.full_name}
          </span>`}
        ${/* Town and region on one line rather than two, because they are
              one answer to "where is this": "Basingstoke · South East"
              reads as a place, and a town stacked over a region reads as a
              form. Either alone is fine; both empty draws nothing. */''}
        ${(l.town || l.region) && html`
          <span class="sub" data-testid=${`league-where-${l.league_id}`}
                style="display:block;text-transform:none">
            ${[l.town, l.region].filter(Boolean).join(' · ')}
          </span>`}
        <span class="sub" style="display:block;text-transform:none">
          ${bits.join(' · ')}
        </span>
        ${/* The season and when it runs. Absent for a league with no
              archive, rather than drawn empty. */''}
        <span class="sub" data-testid=${`league-season-${l.league_id}`}
              style="display:block;text-transform:none">
          ${f.season
            ? (f.runs_from && f.runs_to
                ? `${f.season} · ${f.runs_from} to ${f.runs_to}`
                : f.season)
            : ''}
        </span>
        ${/* "Whether it's currently accepting registrations". A LABEL and
              nothing else -- Ben was asked whether it should gate invite
              codes and said it should not, so a closed league's codes still
              work and this sentence is the whole feature. Drawn either way,
              unlike the lines above: "not taking new teams" is an answer
              somebody came to this box for, and leaving it out when false
              would make a closed league look like one that had not said. */''}
        <span class="sub" data-testid=${`league-registrations-${l.league_id}`}
              style="display:block;text-transform:none">
          ${l.accepting_registrations
            ? 'Accepting registrations'
            : 'Not accepting registrations'}
        </span>
      </span>
      ${/* "On the right of the box should be a box space for a league
            crest/badge." The space is always there so every card is the
            same shape; `Picture` draws nothing until one is approved, the
            same rule venues and teams follow. */''}
      <span data-testid=${`league-crest-${l.league_id}`}
            style="width:3rem;height:3rem;flex:0 0 3rem;
                   display:flex;align-items:center;justify-content:center">
        <${Picture} imageId=${l.crest} alt="" className="avatar"
                    style="width:3rem;height:3rem" />
      </span>
    </button>`;
}

export function LeaguesScreen({ onGo }) {
  const [mine, error] = useRead('/api/leagues');

  if (error) {
    return html`
      <div class="stack">
        <h2>Leagues</h2>
        <p class="error" data-testid="leagues-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (mine === undefined) return html`<p class="sub">Loading…</p>`;

  return html`
    <div class="stack" data-testid="leagues-screen">
      <h2>Leagues</h2>

      ${mine.length === 0
        ? html`
          <p class="sub" data-testid="leagues-none">
            You are currently not registered in a league. To register for a
            local or regional league, please liaise with the the relevant
            league administrators who will provide you an invitational code
            that you can enter below
          </p>
          <${CodeBox} />`
        : html`
          <div class="card stack" data-testid="leagues-another">
            <label>Register to another league</label>
            <${CodeBox} />
          </div>

          <div class="stack" data-testid="leagues-list">
            <label>Your leagues</label>
            ${mine.map((l) => html`<${LeagueCard} league=${l} onGo=${onGo} />`)}
          </div>`}

      ${/* "Underneath the your leagues section should be a new section
            which has UK regions." Underneath BOTH arms, so somebody in no
            league yet -- who is the person most in need of finding one --
            gets it too. */''}
      <${Regions} onGo=${onGo} />
    </div>`;
}

/**
 * Browsing for a league by where it is — issue #72.
 *
 * ONLY LEAGUES THAT ASKED TO BE FOUND. Nick's answer to "does browsing make
 * leagues publicly visible" was that each league opts in, so this is the one
 * place in the app that shows leagues you are not in, and it shows only the
 * ones whose committee said it could. A private works league is absent from
 * it exactly as if it did not exist — see `browse_leagues` in api.py.
 *
 * The regions arrive with counts and every one is listed, including the
 * empty ones: a list that hid them would change shape as leagues came and
 * went, and somebody looking for their own area could not tell "nobody here
 * yet" from "we do not cover that".
 *
 * Fetched WHEN OPENED, not with the page. Twelve regions is twelve possible
 * requests and eleven of them are for areas nobody on this phone lives in.
 */
function Regions({ onGo }) {
  const [regions] = useRead('/api/leagues/regions');
  const [open, setOpen] = useState('');
  const [found, setFound] = useState({});

  const toggle = async (region) => {
    if (open === region) { setOpen(''); return; }
    setOpen(region);
    if (found[region] !== undefined) return;
    try {
      setFound({ ...found,
                 [region]: await get(
                   `/api/leagues/browse?region=${encodeURIComponent(region)}`) });
    } catch (e) {
      // A region that will not load is left unanswered rather than shown as
      // empty: "no leagues here" and "we could not ask" are different
      // things, and only one of them means stop looking.
      setFound({ ...found, [region]: null });
    }
  };

  if (!regions || regions.length === 0) return null;
  return html`
    <div class="stack" data-testid="leagues-regions">
      <label>Find a league near you</label>
      ${regions.map((r) => html`
        <div key=${r.region} class="stack" style="gap:.35rem">
          <button class="secondary card rowbetween" style="width:100%"
                  aria-expanded=${open === r.region}
                  data-testid=${`region-${r.region}`}
                  onClick=${() => toggle(r.region)}>
            <span>${r.region}</span>
            <span class="mono sub">${r.leagues}</span>
          </button>
          ${open === r.region && html`
            <div class="stack" data-testid=${`region-open-${r.region}`}>
              ${found[r.region] === undefined
                ? html`<p class="sub">Loading…</p>`
                : found[r.region] === null
                  ? html`<p class="error">Could not ask just now.</p>`
                  : found[r.region].length === 0
                    ? html`<p class="empty">No leagues listed here yet.</p>`
                    : found[r.region].map((l) => html`
                        <${LeagueCard} league=${l} onGo=${onGo} />`)}
            </div>`}
        </div>`)}
    </div>`;
}

/**
 * The invite code box.
 *
 * The same control in both states of the page, which is why it is one thing
 * rather than two that have to be kept saying the same words. It asks for no
 * refresh of its own: every write in this app clears the read cache, so the
 * list above re-reads itself the moment the code lands.
 */
function CodeBox() {
  const [code, setCode] = useState(
    () => new URLSearchParams(location.search).get(LEAGUE_CODE_PARAM) || '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  // TAKEN OUT OF THE URL once it is in the box -- issue #89. A scanned code
  // is read exactly once, on mount, and then the address is rewritten
  // without it. Leaving it there puts a live invite in the address bar and
  // in history, and makes a refresh re-post a code the person has by then
  // already redeemed, which answers "you are already in this league" to
  // somebody who did nothing wrong. `replaceState` rather than `pushState`:
  // this is a correction to the current entry, not a place to go back to.
  useEffect(() => {
    const url = new URL(location.href);
    if (!url.searchParams.has(LEAGUE_CODE_PARAM)) return;
    url.searchParams.delete(LEAGUE_CODE_PARAM);
    history.replaceState(null, '', url.toString());
  }, []);

  const join = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setFailed('');
    try {
      await post('/api/leagues/join', { code: code.trim() });
      setCode('');
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="stack">
      <div class="row pair">
        <input data-testid="league-code" value=${code}
               placeholder="Invitational code" autocapitalize="off"
               autocomplete="off" spellcheck="false"
               onInput=${(e) => setCode(e.target.value)}
               onKeyDown=${(e) => { if (e.key === 'Enter') join(); }} />
        <button data-testid="league-join" disabled=${busy || !code.trim()}
                onClick=${join}>${busy ? 'Joining…' : 'Register'}</button>
      </div>
      ${failed && html`<p class="error" data-testid="league-join-error">
                         ${failed}
                       </p>`}
    </div>`;
}

// Ben's rooms. Home, Match Hub, Competitions and Standings across the top;
// Stats, Venues and Committee under them. The ones with nothing behind them
// yet say so when opened rather than being hidden, because a league page with
// four buttons on it is not the page that was specified.
const ROOMS = [
  { id: 'home', label: 'Home' },
  { id: 'fixtures', label: 'Match Hub' },
  { id: 'cups', label: 'Competitions' },
  { id: 'table', label: 'Standings' },
  { id: 'stats', label: 'Stats' },
  { id: 'venues', label: 'Venues' },
  { id: 'committee', label: 'Committee' },
];

/**
 * One league: its rooms, and the way back.
 *
 * Every room now has something behind it — Competitions was the last one,
 * filled in by Task 3. The fallback below stays rather than being deleted:
 * the page Ben specified keeps growing, and a room added to `ROOMS` without
 * a matching branch here says so when opened instead of rendering nothing.
 */
export function LeagueScreen({ leagueId, room: routed, onGo, onDone }) {
  // The room comes from the URL, defaulting to Home when the address names
  // none. It used to be `useState('home')`, so the room survived nothing
  // that left the screen: back from a team page restored the league but not
  // the table you had been reading (issue #25), a reload dropped you on
  // Home, and "here is the Division 1 table" was not a link anyone could
  // send. Rooms are places, so they belong in the address.
  //
  // Home is the bare `#/league/{id}`, not `.../home`: it is where the screen
  // opens, and one place should not have two addresses.
  const room = routed || 'home';
  const setRoom = (next) => onGo('league', leagueId,
                                 next === 'home' ? null : next);
  // Which Committee sub-page, held HERE rather than inside Committee: the
  // rooms nav has to be able to clear it. Pressing Committee while already
  // in Committee never unmounts it, so a sub-page opened earlier stayed
  // open and the room's own buttons were nowhere to be found.
  const [committeePage, setCommitteePage] = useState(null);
  const [league, error] = useRead(`/api/leagues/${encodeURIComponent(leagueId)}`);

  const answer = (ask, accept) => post(
    `/api/leagues/${encodeURIComponent(leagueId)}/entries/`
    + `${encodeURIComponent(ask.kind)}/${encodeURIComponent(ask.subject_id)}`,
    { accept });

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="league-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (league === undefined) return html`<p class="sub">Loading…</p>`;

  return html`
    <div class="screen" data-testid="league-screen">
      <div class="rowbetween">
        <h2 data-testid="league-title">${league.name}</h2>
        <span class="row" style="width:auto">
          ${/* Documents are specified and not built. Shown as plainly not
                ready rather than left off, so the row Ben drew is the row
                that is there. Search is built (issue #29). */''}
          <button class="secondary" style="width:auto"
                  aria-current=${room === 'search' ? 'page' : undefined}
                  data-testid="league-search" aria-label="Search"
                  onClick=${() => setRoom(room === 'search'
                                          ? 'home' : 'search')}>🔍</button>
          <button class="secondary" style="width:auto" disabled
                  data-testid="league-docs" aria-label="Documents">📎</button>
          ${/* GitHub issue #77. HERE rather than in the chrome: this screen
                takes the whole window and draws its own row, so `Shell` --
                where one control for everywhere would live -- is not on
                screen at all. A league's rooms are the pages somebody
                actually wants a shortcut to. */''}
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="league-back" onClick=${onDone}>Back</button>
        </span>
      </div>

      <nav class="rooms" data-testid="league-rooms">
        ${ROOMS.map((r) => html`
          <button key=${r.id} class=${room === r.id ? '' : 'secondary'}
                  aria-current=${room === r.id ? 'page' : undefined}
                  data-testid=${`league-go-${r.id}`}
                  onClick=${() => { setRoom(r.id); setCommitteePage(null); }}>
            ${r.label}
          </button>`)}
      </nav>

      ${room === 'home' ? html`<${Home} league=${league} answer=${answer}
                                        leagueId=${leagueId} onGo=${onGo} />`
        : room === 'fixtures' ? html`<${MatchHub} leagueId=${leagueId}
                                                  league=${league}
                                                  onGo=${onGo} />`
        : room === 'cups' ? html`<${Competitions} leagueId=${leagueId}
                                                  onGo=${onGo} />`
        : room === 'table' ? html`<${Standings} leagueId=${leagueId}
                                                league=${league}
                                                onGo=${onGo} />`
        : room === 'stats' ? html`<${Stats} leagueId=${leagueId}
                                            onGo=${onGo} />`
        : room === 'venues' ? html`<${Venues} league=${league} onGo=${onGo} />`
        : room === 'committee' ? html`<${Committee} league=${league}
                                                   leagueId=${leagueId}
                                                   page=${committeePage}
                                                   setPage=${setCommitteePage}
                                                   onGo=${onGo} />`
        : room === 'search' ? html`<${Search} leagueId=${leagueId}
                                                onGo=${onGo} />`
        // A place, not a mode: reached from a button on Home rather than the
        // rooms nav (Ben asked for it there), but rendered where a room is,
        // so it gets an address, a Back and a reload for free -- the same
        // bargain search made.
        : room === 'calendar' ? html`<${LeagueCalendar} leagueId=${leagueId}
                                                        onGo=${onGo} />`
        : html`<p class="empty" data-testid="league-soon">
                 ${`${(ROOMS.find((r) => r.id === room) || {}).label} is not `}
                 built yet.
               </p>`}
    </div>`;
}

/**
 * Finding a team, a player or a pub in this league — GitHub issue #29.
 *
 * Three lists, not one ranked one. A team, a person and a pub are different
 * things to be looking for, and a single list ordered by how well the text
 * matched would bury the pub you meant under three players whose names
 * happen to be closer to what you typed.
 *
 * Typed into, so it asks on a delay rather than per keystroke: "Wheatsheaf"
 * is eleven requests answered by one. `useRead` is not used here for the
 * same reason -- the query is not a place, it is what somebody is in the
 * middle of typing.
 */
function Search({ leagueId, onGo }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);

  useEffect(() => {
    // Under the minimum the endpoint answers nothing, so do not ask: the
    // first keystroke of every search would otherwise be a request whose
    // answer is known to be empty.
    if (q.trim().length < 2) { setHits(null); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      get(`/api/leagues/${encodeURIComponent(leagueId)}`
          + `/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (live) setHits(r); })
        // A search that fails should say nothing rather than take the room
        // with it -- you can always type again.
        .catch(() => { if (live) setHits(null); });
    }, 250);
    // Cleared on the next keystroke, so an answer to an older query cannot
    // land after a newer one and overwrite it.
    return () => { live = false; clearTimeout(timer); };
  }, [leagueId, q]);

  const groups = hits ? [
    ['teams', 'Teams', hits.teams,
     (t) => (t.circle_id ? () => onGo('team', leagueId, t.circle_id) : null),
     (t) => [t.venue, t.division].filter(Boolean).join(' · ')],
    ['players', 'Players', hits.players,
     (p) => () => onGo('player', leagueId, p.player_key), () => ''],
    ['venues', 'Venues', hits.venues,
     (v) => () => onGo('venue', v.venue_id), (v) => v.postcode || ''],
  ].filter(([, , rows]) => rows.length > 0) : [];

  return html`
    <div class="stack" data-testid="league-search-room">
      <input data-testid="league-search-box" value=${q} autofocus
             placeholder="A team, a player or a pub"
             onInput=${(e) => setQ(e.target.value)} />

      ${q.trim().length >= 2 && hits && groups.length === 0 && html`
        <p class="empty" data-testid="league-search-none">
          ${`Nothing in this league matches "${q.trim()}".`}
        </p>`}

      ${groups.map(([id, label, rows, press, detail]) => html`
        <div class="stack" key=${id} data-testid=${`league-search-${id}`}>
          <label>${label}</label>
          ${rows.map((r) => {
            const go = press(r);
            const line = detail(r);
            const inner = html`
              <span style="text-align:left">
                <span style="display:block">${r.name}</span>
                ${line && html`<span class="sub"
                                     style="text-transform:none">${line}</span>`}
              </span>`;
            // A hit with nowhere to go is still worth SHOWING -- a mirrored
            // team nobody has linked to a circle has no page yet, and the
            // honest answer is the name without a button round it, the same
            // rule Standings follows for exactly the same rows.
            return go
              ? html`<button key=${r.name} class="secondary card rowbetween"
                             data-testid=${`league-search-hit-${id}-${
                               r.name.replace(/\s+/g, '-')}`}
                             onClick=${go}>${inner}</button>`
              : html`<div key=${r.name} class="card rowbetween"
                          data-testid=${`league-search-hit-${id}-${
                            r.name.replace(/\s+/g, '-')}`}>${inner}</div>`;
          })}
        </div>`)}
    </div>`;
}



/**
 * The league's noticeboard.
 *
 * GitHub issue #36. A committee announcing a presentation night or a rule
 * change is a fact about the league that outlives the day, so it is posted
 * and kept rather than said in a chat that scrolls away.
 *
 * READ by everybody, WRITTEN by a committee. The server decides that --
 * `may_admin` is `league.member.write`, which is what running a league
 * already means here -- and this only shows or hides the form. A screen that
 * decided for itself would be a second opinion about authority, and the two
 * would drift.
 */
function LeagueBoard({ leagueId, league }) {
  const [writing, setWriting] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [at, setAt] = useState('');
  const [error, setError] = useState('');
  const [round, setRound] = useState(0);
  const notices = league.bulletins || [];

  const send = async () => {
    setError('');
    try {
      await post(`/api/leagues/${encodeURIComponent(leagueId)}/bulletins`,
                 { kind: at ? 'event' : 'note', title, body, at: at || null });
      setWriting(false); setTitle(''); setBody(''); setAt('');
      setRound((n) => n + 1);
      location.reload();
    } catch (e) { setError(e.detail || e.message); }
  };

  const drop = async (id) => {
    setError('');
    try {
      await del(`/api/leagues/${encodeURIComponent(leagueId)}/bulletins/`
                + encodeURIComponent(id));
      location.reload();
    } catch (e) { setError(e.detail || e.message); }
  };

  // Nothing at all when there is nothing to read and nothing you may do --
  // a heading over an empty board tells an ordinary member about a feature
  // that is not theirs.
  if (!notices.length && !league.may_admin) return null;

  return html`
    <div class="stack" data-testid="league-board">
      <div class="rowbetween">
        <label>Noticeboard</label>
        ${league.may_admin && html`
          <button class="secondary" style="width:auto"
                  data-testid="league-board-post"
                  onClick=${() => setWriting(!writing)}>
            ${writing ? 'Cancel' : 'Post'}
          </button>`}
      </div>

      ${error && html`<p class="error" data-testid="league-board-error">
                        ${error}
                      </p>`}

      ${writing && html`
        <div class="stack" data-testid="league-board-form">
          <input data-testid="league-board-title" value=${title}
                 placeholder="What is happening"
                 onInput=${(e) => setTitle(e.target.value)} />
          <input data-testid="league-board-body" value=${body}
                 placeholder="Anything else worth saying"
                 onInput=${(e) => setBody(e.target.value)} />
          ${/* A date makes it an EVENT rather than a note. Left empty it is
                a notice, which is what most of them are. */''}
          <input data-testid="league-board-at" type="date" value=${at}
                 onInput=${(e) => setAt(e.target.value)} />
          <button data-testid="league-board-send" onClick=${send}>
            Put it up
          </button>
        </div>`}

      ${notices.length === 0
        ? html`<p class="empty" data-testid="league-board-empty">
                 Nothing on the board.
               </p>`
        : notices.map((b) => html`
          <div class="card" key=${b.bulletin_id}
               data-testid=${`league-notice-${b.bulletin_id}`}>
            <div class="rowbetween">
              <span style="text-align:left;min-width:0">
                <span style="display:block">${b.title}</span>
                ${b.body && html`<span class="sub"
                                       style="text-transform:none">
                                   ${b.body}
                                 </span>`}
              </span>
              <span class="sub mono" style="white-space:nowrap;
                                            text-align:right">
                ${b.at || String(b.posted_at || '').slice(0, 10)}
              </span>
            </div>
            <p class="sub" style="text-transform:none;margin:.3rem 0 0">
              ${`Posted by ${b.posted_by_name || 'the committee'}`}
              ${league.may_admin && html`
                ${' · '}
                <button class="linky"
                        data-testid=${`league-notice-remove-${b.bulletin_id}`}
                        onClick=${() => drop(b.bulletin_id)}>Take it down</button>`}
            </p>
          </div>`)}
    </div>`;
}

/** Where it stands, and the committee's queue if you are on it. */
/**
 * Leaving a league you are in.
 *
 * The command and the endpoint have existed since the league screens were
 * built; there was simply no way to press it, so somebody who joined with the
 * wrong code was in for ever.
 *
 * Two presses, because the consequence is a locked door rather than a lost
 * record: a league you are not in reads as one that does not exist (that is
 * `_league_reader`, and it is right), so leaving means the page stops
 * loading. Nothing is deleted -- rejoining is another code away, and every
 * result you played stays in the log either way.
 */
function LeaveLeague({ league, leagueId, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const go = async () => {
    setBusy(true);
    setFailed('');
    try {
      await del(`/api/leagues/${encodeURIComponent(leagueId)}`
                + `/members/${encodeURIComponent(league.me)}`);
      onDone();
    } catch (e) {
      setFailed(e.detail || e.message);
      setBusy(false);
    }
  };

  return html`
    <div class="stack" data-testid="league-leave-room">
      ${confirming
        ? html`
          <p class="sub" style="margin:0" data-testid="league-leave-warn">
            You will stop being able to open this league. Nothing you have
            played is lost, and a code lets you back in.
          </p>
          <div class="row pair">
            <button class="secondary" data-testid="league-leave-cancel"
                    disabled=${busy}
                    onClick=${() => setConfirming(false)}>Stay</button>
            <button class="secondary" data-testid="league-leave-confirm"
                    disabled=${busy}
                    style="color:var(--danger);border-color:var(--danger)"
                    onClick=${go}>
              ${busy ? 'Leaving…' : 'Yes, leave'}
            </button>
          </div>`
        : html`
          <button class="secondary" data-testid="league-leave-start"
                  onClick=${() => setConfirming(true)}>
            Leave this league
          </button>`}
      ${failed && html`<p class="error" data-testid="league-leave-error">
                         ${failed}
                       </p>`}
    </div>`;
}

function Home({ league, answer, onGo, leagueId }) {
  return html`
    <div class="stack" data-testid="league-home">
      ${/* Issue #33. On Home rather than in the rooms nav, where Ben asked
            for it -- the nav is already seven wide on a phone. */''}
      <button class="secondary" data-testid="league-go-calendar"
              onClick=${() => onGo('league', leagueId, 'calendar')}>
        <${Icon} name="calendar" size=${18} />
        <span>Calendar</span>
      </button>

      ${/* The league's noticeboard (issue #36). Everybody reads it; only a
            committee may post, which is `may_admin` -- the same capability
            the league's own admin controls are drawn on, so the line is
            drawn once rather than a second time for a board. */''}
      <${LeagueBoard} leagueId=${leagueId} league=${league} />
      <p class="sub" data-testid="league-members">
        ${`${league.members} registered · ${league.venues.length} `}
        ${league.venues.length === 1 ? 'venue' : 'venues'}
        ${` · ${league.teams.length} `}
        ${league.teams.length === 1 ? 'team' : 'teams'}
        ${league.source === 'external'
          ? ' · results come from the league itself'
          : ' · run here'}
      </p>

      ${/* When the league's own record was last read in -- issue #93. Its
            own line under the counts rather than a fourth clause on them:
            those are facts about the LEAGUE, and this is a fact about our
            COPY of it -- the one that says whether to believe the rest.

            Only for a mirrored league, and only once something has been
            projected. A league run HERE is not read in from anywhere and
            has nothing to be behind, so dating it would invite "behind
            what?", which has no answer. `facts` is empty for one of those
            already, so this is off by construction rather than by a second
            test of `source` that could drift from the first. */''}
      ${league.facts && league.facts.updated && html`
        <p class="sub" data-testid="league-updated">
          ${`Updated ${when(league.facts.updated)}`}
        </p>`}

      ${/* A team the committee has accepted but no season has placed yet has
            no standings row — leagueproject only seeds one for a circle
            already in a division — so without this it is nowhere on the
            page at all. Empties itself the moment a season places it, and
            never duplicates the row standings then owns. */''}
      ${league.teams.some((t) => !t.placed) && html`
        <div class="stack" data-testid="league-unplaced">
          <label>Not yet placed</label>
          ${league.teams.filter((t) => !t.placed).map((t) => html`
            <div class="card rowbetween" key=${t.circle_id}
                 data-testid=${`league-unplaced-${t.circle_id}`}>
              <span>${t.name}</span>
              <span class="sub">${t.venue}</span>
            </div>`)}
        </div>`}

      ${/* An empty league is the state every league starts in, and what to do
            about it is not guessable: the pub applies, not the league. Saying
            so here is cheaper than somebody working it out. */''}
      ${league.venues.length === 0 && html`
        <p class="sub" data-testid="league-empty-next">
          Nothing plays here yet. A pub applies from its own venue page —
          Venues, then the pub, then Apply — and it appears below for you to
          accept. Its teams are entered the same way once it is in.
        </p>`}

      ${/* A heading, not a status. "Waiting to be let in" read as though it
            were about the person looking at it, which on a brand-new league
            with nothing in it is the only way to read it. */''}
      ${league.may_answer && html`
        <div class="stack" data-testid="league-pending">
          <label>Pubs asking to join</label>
          ${(league.pending || []).length === 0
            ? html`<p class="empty" data-testid="league-none-pending">
                     Nobody has applied yet.
                   </p>`
            : league.pending.map((ask) => html`
                <div class="card rowbetween"
                     key=${`${ask.kind}-${ask.subject_id}`}
                     data-testid=${`league-ask-${ask.subject_id}`}>
                  <span>
                    <span style="display:block">${ask.name}</span>
                    <span class="sub">
                      ${ask.kind === 'venue' ? 'a venue' : 'a team'}
                    </span>
                  </span>
                  <span class="row" style="width:auto">
                    ${ask.kind === 'venue' && html`
                      <button class="secondary" style="width:auto"
                              data-testid=${`league-look-${ask.subject_id}`}
                              onClick=${() => onGo('venue', ask.subject_id)}>
                        Look
                      </button>`}
                    <button class="secondary" style="width:auto"
                            data-testid=${`league-yes-${ask.subject_id}`}
                            onClick=${() => answer(ask, true)}>Accept</button>
                    <button class="secondary" style="width:auto"
                            data-testid=${`league-no-${ask.subject_id}`}
                            onClick=${() => answer(ask, false)}>Reject</button>
                  </span>
                </div>`)}
        </div>`}

      ${/* Last on the page, deliberately: it is the one control here you
            would not want to hit while reaching for something else. */''}
      ${league.member && html`<${LeaveLeague} league=${league}
                                              leagueId=${leagueId}
                                              onDone=${() => onGo('home')} />`}
    </div>`;
}

function Venues({ league, onGo }) {
  return html`
    <div class="stack" data-testid="league-venues">
      <label>Venues</label>
      ${league.venues.length === 0
        ? html`<p class="empty">No venues in it yet.</p>`
        : league.venues.map((v) => { const line = venueLine(v); return html`
            <button key=${v.venue_id} class="secondary card rowbetween"
                    data-testid=${`league-venue-${v.venue_id}`}
                    onClick=${() => onGo('venue', v.venue_id)}>
              <span style="text-align:left;min-width:0">
                <span style="display:block">${v.name}</span>
                ${line && html`
                  <span class="sub" style="text-transform:none"
                        data-testid=${`league-venue-detail-${v.venue_id}`}>
                    ${line}
                  </span>`}
              </span>
              ${/* Issue #27, and the screen Ben screenshotted. The same shape
                    as the Venues tab's rows: these two lists are the same
                    thing seen from two places and were deliberately made to
                    match, so they move together. */''}
              ${v.postcode && html`
                <span class="sub" style="text-align:right;white-space:nowrap;
                                         text-transform:none"
                      data-testid=${`league-venue-postcode-${v.venue_id}`}>
                  ${v.postcode}
                </span>`}
            </button>`; })}
    </div>`;
}

/** Who runs it. Grant holders, the same shape as a venue's authority panel. */
/**
 * Running the league, in one place.
 *
 * GitHub issue #13. The season, the fixture form and the team matching were
 * three rooms apart -- Season had a top-level button of its own, "Put a
 * fixture in" sat above the Match Hub's own results, and matching lived here
 * in a wall of controls. They are the same JOB, done by the same handful of
 * people, and everything else on the league page is for everybody.
 *
 * Sub-pages rather than one long screen: each of these is a sitting's work,
 * not a glance, and stacking them meant the committee scrolled past two to
 * reach the third. Local state, like `room` above -- rooms are not in the URL
 * here, so a sub-page is not either, and inventing a route for one would be
 * the only part of this page that had one.
 */
const COMMITTEE_PAGES = [
  { id: 'season', label: 'Season' },
  { id: 'fixtures', label: 'Fixtures' },
  { id: 'teams', label: 'Teams in the league' },
  // GitHub issue #46. The registered roster used to sit at the bottom of the
  // committee screen, below the codes and the team generator, so finding a
  // person meant scrolling past two jobs that are nothing to do with them --
  // and it grows without limit while everything above it is fixed in size.
  { id: 'players', label: 'Players in the league' },
  // GitHub issue #69, which is #46 again for the other list on this screen.
  // Who runs the league sat below the codes and the team generator, so the
  // one section that is about PEOPLE was the furthest thing down a screen of
  // jobs -- and like the roster above it, it is the part that grows.
  { id: 'roles', label: 'Who runs this league' },
];

/**
 * Undo a league that should not exist.
 *
 * Admin only, and it says why when it cannot rather than offering a button
 * that answers "you may not" -- the same shape as deleting a circle, and the
 * same reason: a league with a fixture in it is not a mistake, it is
 * somebody's season, and "delete" is far too easy a word for that.
 *
 * Two presses. The first is a decision, the second is the act.
 */
function DeleteLeague({ league, leagueId, onGo }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const go = async () => {
    setBusy(true);
    setFailed('');
    try {
      await del(`/api/leagues/${encodeURIComponent(leagueId)}`);
      onGo('home');
    } catch (e) {
      setFailed(e.detail || e.message);
      setBusy(false);
    }
  };

  return html`
    <div class="stack" data-testid="league-delete-room">
      <label>Delete this league</label>
      ${league.fixtures > 0
        ? html`
          <p class="sub" data-testid="league-delete-blocked">
            ${`This league has ${league.fixtures} fixture${
                league.fixtures === 1 ? '' : 's'} in it. Take them out first —
               a league that has been played in is somebody's season, not a
               mistake.`}
          </p>`
        : html`
          <div class="stack" data-testid="league-delete">
            <p class="sub" style="margin:0">
              Takes it off every list. What is already in the log stays there;
              this is for one created in error.
            </p>
            ${confirming
              ? html`
                <div class="row pair">
                  <button class="secondary" data-testid="league-delete-cancel"
                          disabled=${busy}
                          onClick=${() => setConfirming(false)}>Cancel</button>
                  ${/* `secondary`, so it is an OUTLINED red button rather
                        than red text on the teal fill a primary button
                        carries -- the fill says "safe default action" while
                        the words say the opposite, which is a poor thing to
                        put on the one control that cannot be undone.
                        (circle.js has the same pair without the class and
                        reads teal-with-red-text; worth the same change.) */''}
                  <button class="secondary" data-testid="league-delete-confirm"
                          disabled=${busy}
                          style="color:var(--danger);border-color:var(--danger)"
                          onClick=${go}>
                    ${busy ? 'Deleting…' : `Yes, delete ${league.name}`}
                  </button>
                </div>`
              : html`
                <button class="secondary" data-testid="league-delete-start"
                        style="color:var(--danger)"
                        onClick=${() => setConfirming(true)}>
                  Delete this league
                </button>`}
          </div>`}
      ${failed && html`<p class="error" data-testid="league-delete-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Handing a league's authority to somebody, and taking it back.
 *
 * `Committee` is the whole thing -- admits pubs and teams, writes the
 * calendar, issues codes, and passes this on. There is deliberately no
 * narrower named role yet: the capabilities exist (`entry.approve`,
 * `fixture.write`, `member.write`) and can be granted through the API, but
 * naming a bundle for each before anybody has asked for one would be
 * inventing roles rather than recording them -- which is the rule
 * bundles.py's own docstring states.
 */
function LeagueRoles({ league, leagueId }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const people = league.people || [];

  // NO `window.location.reload()`. It was the only one left in this file,
  // and moving this section onto a page of its own is what made it show:
  // the sub-page somebody is standing on is component state, so reloading
  // the document threw them back out to Committee the instant they gave
  // anybody a role -- the change they had just made scrolled off with the
  // screen it happened on.
  //
  // Nothing is lost by dropping it. `post` fires `onMutation`, which clears
  // the read cache and tells every `useRead` to ask again, so the league
  // this section draws from is refetched anyway and the row redraws with
  // the bundle it just gained. See read.js.
  const act = (fn) => async () => {
    setBusy(true); setFailed('');
    try { await fn(); }
    catch (e) { setFailed(e.detail || e.message); }
    finally { setBusy(false); }
  };

  const give = (userId) => act(() => post('/api/grants', {
    subject_id: userId, scope_type: 'league', scope_id: leagueId,
    capability: '*', cascade: false,
  }));

  const takeBack = (userId) => act(() => post('/api/grants/revoke', {
    subject_id: userId, scope_type: 'league', scope_id: leagueId,
    capability: '*',
  }));

  return html`
    <div class="card stack" data-testid="league-roles">
      <label>Who runs this league</label>
      ${/* WHERE THESE NAMES COME FROM, said out loud and always -- not only
            when the list is empty. A role here can only go to somebody who
            is IN the league, which is right, but somebody hunting for a name
            that is not on the list has no way to tell a boundary from a bug.
            venue.js has the same rule and says it only when nobody at all is
            grantable; the question people actually ask is "why is JACK not
            here", and that happens with a full list. */''}
      <p class="sub" data-testid="league-roles-note"
         style="text-transform:none">
        Only people in the league can be given a role. Send somebody a code
        above to bring them in first.
      </p>
      ${people.length === 0
        ? html`<p class="empty" data-testid="league-roles-none">
                 Nobody is in this league yet.
               </p>`
        : people.map((p) => html`
            <div key=${p.user_id} class="card rowbetween"
                 data-testid=${`league-role-${p.user_id}`}>
              <span>
                <${RoleMarks} roles=${p.roles} only=${1} />${p.name}
                ${p.bundle && html`<span class="sub"> · ${p.bundle}</span>`}
              </span>
              ${p.bundle
                ? html`<button class="secondary" style="width:auto"
                               disabled=${busy}
                               data-testid=${`league-role-clear-${p.user_id}`}
                               onClick=${takeBack(p.user_id)}>
                         Take it away
                       </button>`
                : html`<button class="secondary" style="width:auto"
                               disabled=${busy}
                               data-testid=${`league-role-give-${p.user_id}`}
                               onClick=${give(p.user_id)}>
                         Runs the league
                       </button>`}
            </div>`)}
      ${failed && html`
        <p class="error" data-testid="league-roles-error">${failed}</p>`}
    </div>`;
}


function Committee({ league, leagueId, page, setPage, onGo }) {

  // A mirrored league's calendar is the other league's, read from the
  // archive, so a fixture written here would go into our log and never
  // appear -- worse than no button. Its teams are matched rather than
  // placed, which is what `Teams in the league` is for.
  // Season is offered to EVERYBODY, and the other two are not. Moving it in
  // here must not cost an ordinary member the ability to see what season it
  // is and which teams are in which division -- it was a top-level room
  // before, readable by anyone in the league, and #13 asked to move it, not
  // to hide it. `SeasonRoom` already withholds its own controls from
  // somebody who cannot use them, which is where that line belongs.
  //
  // Writing a fixture and matching up teams are committee acts with nothing
  // to read behind them, so those stay gated.
  // `players` is offered to everybody for the same reason `season` is: the
  // roster was readable by anyone who could open Committee, and moving it to
  // a page of its own must not quietly make it a committee-only list. Linking
  // a member to their archive record stays gated inside the page, on
  // `may_admin`, exactly where it was.
  // `roles` carries its own gate, `may_grant` -- the SAME answer that used
  // to decide whether the section was drawn inline at the bottom of this
  // screen. It is deliberately not folded into the `may_answer` chain
  // below: being able to run a league's fixtures is not being able to say
  // who else runs it, and the two have never been the same permission.
  const offered = COMMITTEE_PAGES.filter((p) => (
    p.id === 'roles'
      ? !!league.may_grant
      : p.id === 'season' || p.id === 'players'
        ? true
        : !league.may_answer
          ? false
          : p.id === 'fixtures'
            ? league.may_fixture && league.source !== 'external'
            : league.source === 'external'));

  if (page) {
    const here = COMMITTEE_PAGES.find((p) => p.id === page) || {};
    return html`
      <div class="stack" data-testid=${`committee-${page}`}>
        <div class="rowbetween">
          <label>${here.label}</label>
          <button class="secondary" style="width:auto"
                  data-testid="committee-back"
                  onClick=${() => setPage(null)}>Committee</button>
        </div>
        ${page === 'season'
          ? html`<${SeasonRoom} leagueId=${leagueId} league=${league} />`
          : page === 'fixtures'
            ? html`<${AddFixture} leagueId=${leagueId} />`
            : page === 'players'
              ? html`<${PlayersRoom} league=${league} leagueId=${leagueId} />`
              : page === 'roles'
                ? html`<${LeagueRoles} league=${league} leagueId=${leagueId} />`
                : html`<${TeamLinks} league=${league} leagueId=${leagueId} />`}
      </div>`;
  }

  return html`
    <div class="stack" data-testid="league-committee">
      ${offered.map((p) => html`
        <button key=${p.id} class="secondary card rowbetween"
                data-testid=${`committee-go-${p.id}`}
                onClick=${() => setPage(p.id)}>
          <span>${p.label}</span>
          <span class="sub">${{
            season: 'Open a season, place teams in divisions',
            fixtures: 'Write a fixture into the calendar',
            teams: "Match the archive's teams to circles here",
            players: 'Who is registered, and their archive record',
            roles: 'Give somebody a role, or take one back',
          }[p.id]}</span>
        </button>`)}

      ${league.may_invite && html`<${Codes} league=${league}
                                            leagueId=${leagueId} />`}

      ${/* THE CREST -- issue #72, and Ben's follow-up asking for it here
            rather than anywhere else: "allow the committee to upload the
            league image / crest from within the committee tab".

            Behind `may_admin`, which is `league.member.write` -- what the
            committee already holds and what already decides how the league
            presents itself, its bulletins included. No new capability; the
            vocabulary in permissions.py is closed, and
            `Service._may_upload_for` keeps the same rule server-side
            whatever this screen chooses to offer.

            No `onDone`. What was just chosen is PENDING and invisible to
            everybody until an admin approves it, so there is nothing for
            the parent to refetch -- and a refetch here would unmount the
            control and take its "sent" message with it, which is the race
            imageupload.js carries a comment about. */''}
      ${league.may_admin && html`
        <div class="stack">
          <${ImageUpload} kind="league" subjectId=${leagueId}
                          label="League crest" />
        </div>`}

      ${/* WHAT THE LEAGUE IS -- issue #72, beside the crest because that is
            the answer to who owns these fields: the league's own committee,
            in the committee tab. Same `may_admin` gate for the same reason
            the crest uses it. */''}
      ${league.may_admin && html`
        <${LeagueDetails} league=${league} leagueId=${leagueId} />`}

      ${/* WHO RUNS IT is a PAGE now -- issue #69, and see COMMITTEE_PAGES.
            It was drawn here, below the codes and the team generator, which
            put the one section about people at the bottom of a screen of
            jobs. `may_grant` still decides it; the gate moved up to
            `offered` with the button rather than changing.

            The section itself is unchanged, including where its names come
            from: the league's OWN MEMBERS rather than every account on the
            instance, the same call venue.js makes about its roster and for
            the reason issue #56 gives -- a role here can only go to
            somebody who is in the league. */''}
      ${league.may_answer && league.source === 'external' && html`
        <${GenerateTeams} league=${league} leagueId=${leagueId} />`}

      ${league.may_delete && html`<${DeleteLeague} league=${league}
                                                   leagueId=${leagueId}
                                                   onGo=${onGo} />`}
    </div>`;
}

/**
 * Generate this season's teams from the archive — GitHub issue #7.
 *
 * A circle per archive team, linked, with a placeholder for every player it
 * lists — the manual work `TeamLinks` below still exists for, done in one
 * press. Safe to press again: the report distinguishes a run that made
 * nothing (already up to date) from one that made everything, which is the
 * whole reason it shows counts on both sides rather than a single message.
 */
function GenerateTeams({ league, leagueId }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const run = async () => {
    setBusy(true);
    setFailed('');
    try {
      setReport(await post(
        `/api/leagues/${encodeURIComponent(leagueId)}/generate`, {}));
    } catch (e) { setFailed(e.detail || e.message); }
    setBusy(false);
  };

  return html`
    <div class="card stack" data-testid="league-generate">
      <label>Generate this season's teams</label>
      <p class="sub" style="margin:0">
        A circle for every team the archive knows this season, linked, and a
        placeholder for every player on it. Only adds what is missing — safe
        to run again once the archive has moved on.
      </p>
      <button class="secondary" style="width:auto"
              data-testid="league-generate-run"
              onClick=${run} disabled=${busy}>
        ${busy ? 'Working…' : 'Generate'}
      </button>
      ${failed && html`<p class="error" data-testid="league-generate-error">
                         ${failed}
                       </p>`}
      ${report && html`
        <p class="sub" data-testid="league-generate-report">
          ${report.teams_created === 0 && report.players_created === 0
            ? 'Nothing to do — already up to date.'
            : `${report.teams_created} team`
              + `${report.teams_created === 1 ? '' : 's'} made, `
              + `${report.teams_skipped} already linked, `
              + `${report.players_created} player`
              + `${report.players_created === 1 ? '' : 's'} added, `
              + `${report.players_skipped} already there.`}
        </p>`}
    </div>`;
}

/**
 * Which circle each of a mirrored league's teams is.
 *
 * The one thing about an imported league a person has to do. BDIPL's book
 * calls a team "Riverdene Royals" and has never heard of Cueforge; until
 * somebody says that those words mean this circle, the name in the table
 * leads nowhere.
 *
 * Only the teams the league has actually accepted are offered, so the control
 * cannot express a link the server would refuse.
 *
 * TWO LISTS, not one (issue #40). Circles were generated for THIS season's
 * teams, so those are the ones that can actually be matched up today; fifteen
 * years of names that no longer play sit behind their own heading rather than
 * burying them. The archive keeps every team it ever saw, and BDIPL's is
 * hundreds long — a matching screen that lists them all is a screen nobody
 * finishes.
 *
 * Historical teams are still MATCHABLE, not read-only: a team that came back
 * under its old name, or a link somebody wants to correct years later, is a
 * real thing to want. They are out of the way, not out of reach.
 */
function TeamLinks({ league, leagueId }) {
  const [teams, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/teams`);
  const [failed, setFailed] = useState('');
  const [showOld, setShowOld] = useState(false);

  const link = async (teamKey, circleId) => {
    setFailed('');
    try {
      if (circleId) {
        await post(`/api/leagues/${encodeURIComponent(leagueId)}/links`,
                   { team_key: teamKey, circle_id: circleId });
      } else {
        await del(`/api/leagues/${encodeURIComponent(leagueId)}/links/`
                  + encodeURIComponent(teamKey));
      }
    } catch (e) { setFailed(e.detail || e.message); }
  };

  // Already spoken for, so the same circle cannot be offered twice — the
  // server refuses it, and a menu that can only be wrong is worse than a
  // shorter one.
  const taken = new Set((teams || []).map((t) => t.circle_id)
    .filter(Boolean));

  // A team the endpoint has not marked either way is treated as current: a
  // payload without the flag is an older server, and hiding every team behind
  // a collapsed heading would be a worse wrong answer than showing them all.
  const now = (teams || []).filter((t) => t.current !== false);
  const past = (teams || []).filter((t) => t.current === false);
  const unmatched = past.filter((t) => !t.circle_id).length;

  const row = (t) => html`
    <div class="stack" key=${t.team_key}
         data-testid=${`league-link-${t.team_key}`}>
      <span class="rowbetween">
        <span>${t.name}</span>
        <span class="mono sub">${t.division || ''}</span>
      </span>
      <select class="combo" value=${t.circle_id || ''}
              data-testid=${`league-link-pick-${t.team_key}`}
              onChange=${(e) => link(t.team_key, e.target.value)}>
        <option value="">Not matched up</option>
        ${(league.teams || [])
          .filter((c) => !taken.has(c.circle_id)
                         || c.circle_id === t.circle_id)
          .map((c) => html`
            <option key=${c.circle_id} value=${c.circle_id}>
              ${c.name}
            </option>`)}
      </select>
    </div>`;

  return html`
    <div class="card stack" data-testid="league-links">
      <label>Teams in this league</label>
      <p class="sub" style="margin:0">
        The league's own names, and which circle here each one is. Its table
        and fixtures lead nowhere until they are matched up.
      </p>
      ${error
        ? html`<p class="error">${error.detail || error.message}</p>`
        : teams === undefined
          ? html`<p class="sub">Loading…</p>`
          : teams.length === 0
            ? html`<p class="empty" data-testid="league-links-none">
                     Nothing imported yet.
                   </p>`
            : html`
              <div class="stack" data-testid="league-links-now">
                ${now.length === 0
                  ? html`<p class="empty" data-testid="league-links-now-none">
                           No teams in the current season.
                         </p>`
                  : now.map(row)}
              </div>

              ${past.length > 0 && html`
                <div class="stack" data-testid="league-links-historical">
                  <button class="secondary" data-testid="league-links-old-toggle"
                          onClick=${() => setShowOld(!showOld)}>
                    ${showOld
                      ? 'Hide historical teams'
                      : `Historical teams (${past.length}${
                          unmatched ? `, ${unmatched} unmatched` : ''})`}
                  </button>
                  ${showOld && html`
                    <p class="sub" style="margin:0">
                      Teams that are not in the current season. Circles were
                      only made for the teams playing now, so most of these
                      have nothing to match to — they are here for the ones
                      that do.
                    </p>`}
                  ${showOld && past.map(row)}
                </div>`}`}
      ${failed && html`<p class="error" data-testid="league-link-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Everybody registered to this league, and a way to find one — issue #46.
 *
 * A PAGE OF ITS OWN, because this list is the only thing on the committee
 * screen with no ceiling on its length. The season, the codes and the team
 * generator are each a fixed handful of controls; the roster is however many
 * people the league has, and it was underneath all of them.
 *
 * The search box is always here rather than appearing past some length, which
 * is the opposite of the choice the admin roster and the venues list make.
 * Those two share a screen with other things, so a box on a short list is
 * furniture in somebody's way. This page has one job, and a box that comes
 * and goes as a league recruits would be the stranger behaviour.
 *
 * It matches the archive name as well as the account name: half the point of
 * the list is who is NOT linked yet, and the other half is checking that the
 * ones who are went to the right record.
 */
function PlayersRoom({ league, leagueId }) {
  const [search, setSearch] = useState('');
  const roster = league.roster || [];
  const query = search.trim().toLowerCase();
  const shown = !query ? roster : roster.filter((p) => (
    `${p.name} ${p.archive_name || ''} ${p.league_key || ''}`
      .toLowerCase().includes(query)));

  return html`
    <div class="stack" data-testid="committee-players-list">
      <input data-testid="league-players-search" value=${search}
             placeholder="Search registered players"
             onInput=${(e) => setSearch(e.target.value)} />

      ${roster.length === 0
        ? html`<p class="empty" data-testid="league-players-empty">
                 Nobody has registered to this league yet.
               </p>`
        : shown.length === 0
          ? html`<p class="empty" data-testid="league-players-none">
                   Nobody here is called that.
                 </p>`
          : shown.map((p) => html`
              <${Registered} key=${p.user_id} person=${p} leagueId=${leagueId}
                             mayLink=${league.may_admin} />`)}

      <p class="sub">
        Who holds what in this league is not shown yet — it is the same list
        of grants a venue has, and it wants the same panel.
      </p>
    </div>`;
}

/**
 * One registered member, and whether they hold their own archive record.
 *
 * A league mirrored from an archive starts with every seat filled by a
 * generated placeholder and every real account holding nothing, so "who is
 * still not linked" is the committee's actual work — and until this list said
 * so, the only way to find out was to open the search and discover somebody's
 * identity had already gone.
 */
function Registered({ person, leagueId, mayLink }) {
  const [open, setOpen] = useState(false);

  if (person.league_key) {
    return html`
      <div class="card rowbetween"
           data-testid=${`league-member-${person.user_id}`}>
        ${/* Issue #59: what they hold, before the name. */''}
        <span><${RoleMarks} roles=${person.roles} />${person.name}</span>
        ${/* The archive name, not a tick: a link to the WRONG player reads
              exactly like a link to the right one otherwise, and this list
              is the only place anybody would notice. */''}
        <span class="sub" data-testid="league-member-linked">
          ${person.archive_name || person.league_key}
        </span>
      </div>`;
  }

  return html`
    <div class="stack" data-testid=${`league-member-${person.user_id}`}>
      <div class="card rowbetween">
        <span>${person.name}</span>
        ${mayLink
          ? html`
            <button class="secondary" style="width:auto"
                    data-testid=${`league-member-link-${person.user_id}`}
                    onClick=${() => setOpen(!open)}>
              ${open ? 'Cancel' : 'Link'}
            </button>`
          : html`<span class="sub">Not linked</span>`}
      </div>
      ${open && html`<${LinkPlayer} person=${person} leagueId=${leagueId}
                                    onDone=${() => setOpen(false)} />`}
    </div>`;
}

/**
 * Hand a member the archive record that is theirs — issue #43.
 *
 * `claim_placeholder` has always been able to do this and the circle screen
 * already offers it, but only to somebody ALREADY on that team's roster,
 * which in a mirrored league is nobody: the seats are all placeholders. So a
 * member sitting in front of the committee could only be sent a single-use
 * code and asked to redeem it — the right object for a player who has not
 * signed up, and a slow one for a person who already has.
 *
 * ONE ROW PER SEAT. A placeholder who turns out for two teams appears twice,
 * because a claim moves the seat in one circle; claiming both is allowed and
 * is what a player who really does play for both should press.
 */
function LinkPlayer({ person, leagueId, onDone }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState('');
  const [failed, setFailed] = useState('');

  const look = async (q) => {
    setQuery(q);
    setFailed('');
    try {
      setFound(await get(
        `/api/leagues/${encodeURIComponent(leagueId)}/placeholders`
        + `?q=${encodeURIComponent(q)}`));
    } catch (e) {
      setFound([]);
      setFailed(e.detail || e.message);
    }
  };

  // Opened, not pressed: the committee wants to see who is available before
  // typing, and the server caps the list rather than returning all 468.
  useEffect(() => { look(''); }, []);

  const claim = async (seat) => {
    setBusy(seat.ghost_id);
    setFailed('');
    try {
      await post(`/api/circles/${encodeURIComponent(seat.circle_id)}/claim`,
                 { ghost_id: seat.ghost_id, user_id: person.user_id });
      onDone();
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(''); }
  };

  return html`
    <div class="card stack" data-testid="league-link-player">
      <span class="sub" style="text-transform:none">
        Which player is ${person.name} in the archive? They join that team,
        and the placeholder stops standing in.
      </span>
      <input data-testid="league-link-search" value=${query}
             placeholder="Search the archive's players"
             onInput=${(e) => look(e.target.value)} />
      ${found.map((seat) => html`
        <button class="secondary card rowbetween"
                key=${`${seat.ghost_id}-${seat.circle_id}`}
                disabled=${!!busy}
                data-testid=${`league-link-pick-${seat.ghost_id}`}
                onClick=${() => claim(seat)}>
          <span>${seat.name}</span>
          <span class="sub">${seat.team}</span>
        </button>`)}
      ${!found.length && !failed && html`
        <p class="sub">Nobody in the archive matches that.</p>`}
      ${failed && html`<p class="error" data-testid="league-link-player-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Codes to hand out.
 *
 * Shown once and never stored — only its hash is kept, like every other
 * invite in this app. A code may be bound to a team, which puts whoever
 * redeems it in that team as well; the ordinary way in is a team's own roster
 * manager doing exactly that for their players.
 */
/**
 * The committee describing its own league — issue #72.
 *
 * ONE FORM, SAVED WHOLE, matching the one event behind it: a committee that
 * has filled in the town but not decided the region saves what it has, and
 * four separate saves would let three land and one fail, leaving a card
 * whose lines disagree about which press worked.
 *
 * Being LISTED is its own button, deliberately apart from the fields. It is
 * the same line `set_venue_listing` draws: describing yourself is not
 * publishing yourself, and a committee filling in a town must not thereby
 * put a private works league on a public map. Nick chose opt-in, so it is
 * off until pressed.
 *
 * The regions come from the server rather than a list held here, because a
 * second copy is one that drifts — and the one that drifts is always the one
 * somebody is reading.
 */
function LeagueDetails({ league, leagueId }) {
  const [regions] = useRead('/api/leagues/regions');
  const [form, setForm] = useState({
    full_name: league.full_name || '',
    town: league.town || '',
    region: league.region || '',
    accepting_registrations: !!league.accepting_registrations,
  });
  const [listed, setListed] = useState(!!league.listed);
  const [saved, setSaved] = useState('');
  const [failed, setFailed] = useState('');

  const at = (bit) => `/api/leagues/${encodeURIComponent(leagueId)}${bit}`;
  const set = (k, v) => { setForm({ ...form, [k]: v }); setSaved(''); };
  const run = async (fn, note) => {
    setFailed(''); setSaved('');
    try { await fn(); setSaved(note); }
    catch (e) { setFailed(e.detail || e.message); }
  };

  return html`
    <div class="stack" data-testid="league-details">
      <label>About this league</label>
      <p class="sub" style="text-transform:none">
        The counts and the season are worked out from the league's own
        records. These are the things only you can say.
      </p>

      <label for="ld-full">Full name</label>
      <input id="ld-full" data-testid="league-details-fullname"
             value=${form.full_name} maxlength="120"
             placeholder="Basingstoke and Deane Invitational Pool League"
             onInput=${(e) => set('full_name', e.target.value)} />

      <label for="ld-town">Town or city</label>
      <input id="ld-town" data-testid="league-details-town"
             value=${form.town} maxlength="80" placeholder="Basingstoke"
             onInput=${(e) => set('town', e.target.value)} />

      <label for="ld-region">Region</label>
      <select id="ld-region" data-testid="league-details-region"
              value=${form.region}
              onChange=${(e) => set('region', e.target.value)}>
        ${/* "Not said" is an option, not an absence: a committee that picked
              a region by mistake needs a way back to having said nothing. */''}
        <option value="">Not said</option>
        ${(regions || []).map((r) => html`
          <option key=${r.region} value=${r.region}
                  selected=${form.region === r.region}>${r.region}</option>`)}
      </select>

      ${/* A LABEL, and the help text says so out loud. Ben was asked whether
            this should gate invite codes and chose the label, so a committee
            switching it off must not be left believing they have closed the
            door. */''}
      <div class="seg" data-testid="league-details-accepting">
        ${[[true, 'Accepting registrations'],
           [false, 'Not accepting']].map(([v, label]) => html`
          <button key=${String(v)}
                  aria-pressed=${form.accepting_registrations === v}
                  data-testid=${`league-details-accepting-${v}`}
                  onClick=${() => set('accepting_registrations', v)}>
            ${label}
          </button>`)}
      </div>
      <p class="sub" style="text-transform:none">
        This says what the box on the Leagues page reads. Invite codes keep
        working either way.
      </p>

      <button class="secondary" data-testid="league-details-save"
              onClick=${() => run(() => put(at('/details'), form), 'Saved.')}>
        Save
      </button>

      <label>Listed in ${form.region || 'its region'}</label>
      <p class="sub" style="text-transform:none">
        ${listed
          ? 'Anybody browsing that region can find this league.'
          : 'Only people you give a code to can find this league.'}
      </p>
      <button class="secondary" data-testid="league-details-listing"
              onClick=${() => run(async () => {
                await put(at('/listing'), { listed: !listed });
                setListed(!listed);
              }, 'Saved.')}>
        ${listed ? 'Take it off the list' : 'List it in its region'}
      </button>

      ${saved && html`<p class="sub" data-testid="league-details-saved">
                        ${saved}
                      </p>`}
      ${failed && html`<p class="error" data-testid="league-details-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * A code the committee has just made, with the QR that carries it — #89.
 *
 * ITS OWN COMPONENT because of `useQrSvg`. The code is rendered
 * conditionally (`code && ...`), and a hook inside a conditional branch is
 * the one thing Preact's hook rules forbid — the call order would change on
 * the render where a code first appears. Lifting the hook into `Codes`
 * instead would draw a QR for every committee member who never presses the
 * button, which is 56 KB of library fetched to show nothing.
 *
 * The QR carries a LINK, not the bare code, for the same reason the forge
 * invite does: the other phone's camera opens the app with the code already
 * in the box, and the sixteen characters are printed underneath anyway for
 * anyone reading it out at a meeting.
 */
function ShownCode({ code, who }) {
  const svg = useQrSvg(leagueInviteUrl(code));
  return html`
    <div class="card" data-testid="league-code-shown">
      <p class="sub" style="margin:0 0 .4rem">
        ${who
          ? `Send this to ${who.name} — it is shown once, it names them,
             and it stops working the moment they use it.`
          : `Hand this out. It is shown once, it lasts 90 days, and it does
             not run out when the first person uses it.`}
      </p>
      ${/* They must already have a Cueforge account: a league code is
            redeemed by `/api/leagues/join`, which needs somebody signed in,
            and registration wants an account invite instead. Saying so here
            is cheaper than a committee wondering why a stranger's phone
            asked for a different code. */''}
      <p class="sub" style="margin:0 0 .6rem; text-transform:none">
        Point their camera at this — they will need a Cueforge account
        already.
      </p>
      <div class="qr" data-testid="league-code-qr"
           dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <code>${code}</code>
    </div>`;
}

function Codes({ league, leagueId }) {
  const [team, setTeam] = useState('');
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  // A code for ONE PERSON: who it names, and the search that found them.
  const [who, setWho] = useState(null);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);

  const look = async (q) => {
    setQuery(q);
    setWho(null);
    if (q.trim().length < 2) { setFound([]); return; }
    try {
      setFound((await get(`/api/league/search?q=${encodeURIComponent(q)}`))
        .slice(0, 6));
    } catch { setFound([]); }
  };

  const make = async () => {
    setBusy(true);
    setFailed('');
    try {
      const r = await post(
        `/api/leagues/${encodeURIComponent(leagueId)}/invites`,
        { circle_id: team || null, player_key: who ? who.player_key : null });
      setCode(r.code);
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="card stack" data-testid="league-codes">
      <label>Invitational code</label>
      <select class="combo" data-testid="league-code-team" value=${team}
              onChange=${(e) => setTeam(e.target.value)}>
        <option value="">The league, no team</option>
        ${/* THE PUB TOO, not just the team. GitHub issue #68: a team's name
              and the pub it plays at are different words, and somebody
              hunting for their team is as likely to be holding one as the
              other -- "the rising sun does not appear in the list", where
              the two teams there are called Double Shot Sun and Up the
              Risers, neither of which says where it plays. At 45 teams that
              is a list nothing can be found in.

              The row already carries `venue` from `_league_card`, so this
              changes what the option SAYS and not what it is worth. NOT the
              empty option above: it names no team, so it has no pub to name,
              and appending to every option would leave it reading "The
              league, no team — ". Issue #53 made this same call for the
              circles list, where a long team name and its pub were competing
              for one line. */''}
        ${(league.teams || []).map((t) => html`
          <option key=${t.circle_id} value=${t.circle_id}>
            ${t.venue ? `${t.name} — ${t.venue}` : t.name}
          </option>`)}
      </select>
      ${/* A code FOR SOMEBODY. Naming a player makes the code single use and
            links them the moment they redeem it, so they arrive holding their
            own record instead of signing up and then hunting for themselves
            in a list. Optional: leave it empty for the code above, which is
            still the one to read out at a meeting. */''}
      <div class="stack" data-testid="league-code-player">
        <span class="sub" style="text-transform:none">
          For one player — optional. Names them, and is used up once.
        </span>
        ${who
          ? html`
            <div class="card rowbetween" data-testid="league-code-who">
              <span>
                <span style="display:block">${who.name}</span>
                <span class="sub">${`${who.games} games`}</span>
              </span>
              <button class="secondary" style="width:auto"
                      data-testid="league-code-clear"
                      onClick=${() => { setWho(null); look(''); }}>
                Anybody
              </button>
            </div>`
          : html`
            <input data-testid="league-code-search" value=${query}
                   placeholder="Search the league's players"
                   onInput=${(e) => look(e.target.value)} />`}
        ${!who && found.map((p) => html`
          <button class="secondary" key=${p.player_key}
                  data-testid=${`league-code-pick-${p.player_key}`}
                  onClick=${() => { setWho(p); setFound([]); }}>
            ${`${p.name} — ${p.games} games`}
          </button>`)}
      </div>

      <button class="secondary" disabled=${busy}
              data-testid="league-code-make" onClick=${make}>
        ${busy ? 'Making…' : who ? `Make a code for ${who.name}` : 'Make a code'}
      </button>
      ${code && html`<${ShownCode} code=${code} who=${who} />`}
      ${failed && html`<p class="error" data-testid="league-code-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * The season and division pickers, and the state behind them.
 *
 * One hook rather than two components' worth of copied wiring, because the
 * Standings room and the Match Hub ask the same question and must not be able
 * to disagree about the answer. It returns nothing to draw when the league has
 * no seasons — which is every league run here, and is not a failure: the log
 * says when a fixture is, and nothing in it says when a season starts.
 */
function useSeason(leagueId) {
  const [book] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/seasons`);
  const [season, setSeason] = useState('');
  const [division, setDivision] = useState('');

  const seasons = (book && book.seasons) || [];
  // The newest season, until somebody picks another. A league page opens on
  // what is happening now, not on 2017.
  const chosen = season || (seasons[0] && seasons[0].season) || '';
  const chosenSeason = seasons.find((s) => s.season === chosen) || {};
  const divisions = chosenSeason.divisions || [];
  // Issue #26. Kept apart from `divisions` all the way through: a cup has no
  // table, and anything that let one reach `inDivision` would open Standings
  // on an empty one -- the failure `readmodel.divisions` documents at length.
  const competitions = chosenSeason.competitions || [];
  const inDivision = division && divisions.includes(division)
    ? division : (divisions[0] || '');

  // The two screens compose their own out of these rather than sharing one
  // picker: Standings needs a season, and the Match Hub does not — its week
  // arrows already say when, and a season select beside them would be two
  // controls for one question that could disagree.
  const seasonPicker = seasons.length === 0 ? null : html`
    <select class="combo" data-testid="league-season" value=${chosen}
            onChange=${(e) => { setSeason(e.target.value); setDivision(''); }}>
      ${seasons.map((s) => html`
        <option key=${s.season} value=${s.season}>${s.season}</option>`)}
    </select>`;

  const divisionPicker = (all) => divisions.length < 2 ? null : html`
    <nav class="rooms" data-testid="league-divisions">
      ${all && html`
        <button class=${division === '' ? '' : 'secondary'}
                aria-current=${division === '' ? 'page' : undefined}
                data-testid="league-division-all"
                onClick=${() => setDivision('')}>All</button>`}
      ${divisions.map((d) => html`
        <button key=${d} class=${d === (all ? division : inDivision)
                                 ? '' : 'secondary'}
                aria-current=${d === (all ? division : inDivision)
                               ? 'page' : undefined}
                data-testid=${`league-division-${d.replace(/\s+/g, '-')}`}
                onClick=${() => setDivision(d)}>${d}</button>`)}
    </nav>`;

  // Issue #26: a season's knockouts, as filters of their own. The endpoint
  // needed nothing -- a cup's name IS its `division` -- so this offers names
  // that always could have been passed and never were.
  //
  // Its own row, under the divisions, rather than more buttons on that one:
  // "Division 1" and "Captains Cup" are different KINDS of answer to "which
  // matches", and a single unbroken row of them says they are the same.
  const competitionPicker = () => competitions.length === 0 ? null : html`
    <nav class="rooms" data-testid="league-competitions">
      ${competitions.map((c) => html`
        <button key=${c} class=${c === division ? '' : 'secondary'}
                aria-current=${c === division ? 'page' : undefined}
                data-testid=${`league-competition-${c.replace(/\s+/g, '-')}`}
                onClick=${() => setDivision(c)}>${c}</button>`)}
    </nav>`;

  return { loaded: book !== undefined, seasons, season: chosen,
           competitions, competitionPicker,
           // Standings always has one; the hub may show every division at
           // once, which is what an empty string means to the endpoint.
           division: inDivision, anyDivision: division,
           seasonPicker, divisionPicker };
}

/**
 * The season: its divisions, and which Teams are in them.
 *
 * TEAM is the word throughout. A team is a circle under the hood and nothing
 * changes about that, but a league page that called them circles would be
 * describing the app's plumbing to somebody who came to look at the pool.
 *
 * Only shown to whoever may run the league. A control that answers "you may
 * not" is worse than one that is not there.
 */
function SeasonRoom({ leagueId, league }) {
  const base = `/api/leagues/${encodeURIComponent(leagueId)}`;
  const [book, error] = useRead(`${base}/seasons`);
  const [season, setSeason] = useState('');
  const [division, setDivision] = useState('');
  const [failed, setFailed] = useState('');

  const run = async (fn) => {
    setFailed('');
    try { await fn(); } catch (e) { setFailed(e.detail || e.message); }
  };

  if (error) return html`<p class="error">${error.detail || error.message}</p>`;
  if (book === undefined) return html`<p class="sub">Loading…</p>`;

  // Not offered for a mirrored league. Its calendar is the other league's,
  // read from an archive — a season written here would go into our log and
  // never appear, which is worse than no button.
  if (league.source === 'external') {
    return html`
      <p class="empty" data-testid="league-season-room">
        This league is run elsewhere. Its seasons and Teams come from its own
        records.
      </p>`;
  }

  return html`
    <div class="stack" data-testid="league-season-room">
      ${league.may_run && html`
        <div class="card stack" data-testid="season-new">
          <label>Open a season</label>
          <div class="row pair">
            <input data-testid="season-name" value=${season}
                   placeholder="Spring 2027" maxlength="40"
                   onInput=${(e) => setSeason(e.target.value)} />
            <button data-testid="season-open" disabled=${!season.trim()}
                    onClick=${() => run(async () => {
                      await post(`${base}/seasons`,
                                 { season: season.trim() });
                      setSeason('');
                    })}>Open it</button>
          </div>
        </div>`}

      ${league.may_run && html`<${CardEditor} leagueId=${leagueId}
                                              league=${league} />`}
      ${league.may_run && html`<${PointsEditor} leagueId=${leagueId}
                                                league=${league} />`}

      ${book.seasons.length === 0
        ? html`<p class="empty" data-testid="season-none">
                 No season yet. A season holds the divisions, and a division
                 holds the Teams.
               </p>`
        : book.seasons.map((s) => html`
            <div class="card stack" key=${s.season}
                 data-testid=${`season-${s.season.replace(/\s+/g, '-')}`}>
              <div class="rowbetween">
                <label>${s.season}</label>
                ${league.may_run && html`
                  <button class="secondary" style="width:auto"
                          data-testid=${`season-close-${
                            s.season.replace(/\s+/g, '-')}`}
                          onClick=${() => run(() => del(
                            `${base}/seasons/${encodeURIComponent(
                                 s.season)}`))}>
                    Close season
                  </button>`}
              </div>

              ${league.may_run && html`
                <div class="row pair">
                  <input data-testid="division-name" value=${division}
                         placeholder="Division 1" maxlength="40"
                         onInput=${(e) => setDivision(e.target.value)} />
                  <button data-testid="division-add"
                          disabled=${!division.trim()}
                          onClick=${() => run(async () => {
                            await post(
                              `${base}/seasons/`
                              + `${encodeURIComponent(s.season)}/divisions`,
                              { division: division.trim() });
                            setDivision('');
                          })}>Add a division</button>
                </div>`}

              ${s.divisions.length === 0
                ? html`<p class="empty">No divisions in it yet.</p>`
                : s.divisions.map((d) => html`
                    <div class="stack" key=${d}>
                      <span class="sub">${d}</span>
                      ${(s.teams[d] || []).map((t) => html`
                        <div class="card rowbetween" key=${t.circle_id}>
                          <span>
                            <span style="display:block">${t.name}</span>
                            <span class="sub">${t.venue}</span>
                          </span>
                          ${league.may_run && html`
                            <button class="secondary" style="width:auto"
                                    onClick=${() => run(() => del(
                                      `${base}/seasons/`
                                      + `${encodeURIComponent(s.season)}`
                                      + `/divisions/${encodeURIComponent(d)}`
                                      + `/teams/${encodeURIComponent(
                                           t.circle_id)}`))}>
                              Take out
                            </button>`}
                        </div>`)}
                      ${league.may_run && html`
                        <select class="combo" value=""
                                data-testid="team-place"
                                onChange=${(e) => e.target.value && run(() =>
                                  post(`${base}/seasons/`
                                       + `${encodeURIComponent(s.season)}`
                                       + `/divisions/`
                                       + `${encodeURIComponent(d)}/teams`,
                                       { circle_id: e.target.value }))}>
                          <option value="">Put a Team in ${d}</option>
                          ${(league.teams || []).map((c) => html`
                            <option key=${c.circle_id} value=${c.circle_id}>
                              ${c.name}
                            </option>`)}
                        </select>`}
                      ${league.may_run && (s.teams[d] || []).length >= 2
                        && html`<${FixtureGenerator} base=${base}
                                                     season=${s.season}
                                                     division=${d} />`}
                    </div>`)}
            </div>`)}

      ${failed && html`<p class="error" data-testid="season-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Draw a division's whole season. Issue #39.
 *
 * PREVIEW FIRST, always, and the button that writes only appears once there
 * is something to look at. A division of ten teams is ninety appends, and the
 * interesting part is not the fixtures — it is the CLASHES, which nobody can
 * see until the draw is made. Showing them and asking is the only honest way
 * to offer this.
 *
 * The nights are shown as well as the fixtures, because a skipped date moves
 * the whole season and a committee needs to see where it ends up, not just
 * that its request was accepted.
 *
 * Dates to skip are a textarea of one date a line rather than a date picker
 * per exception: bank holidays, a venue's own closures and cup weeks arrive
 * as a list somebody already has, and six pickers to fill in is how a feature
 * goes unused.
 */
function FixtureGenerator({ base, season, division }) {
  const url = `${base}/seasons/${encodeURIComponent(season)}`
            + `/divisions/${encodeURIComponent(division)}/fixtures`;
  const [start, setStart] = useState('');
  const [every, setEvery] = useState('7');
  const [double, setDouble] = useState(true);
  const [skip, setSkip] = useState('');
  const [drawn, setDrawn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const [done, setDone] = useState('');

  const body = (preview) => ({
    start,
    every_days: Math.max(1, parseInt(every, 10) || 7),
    double,
    // Blank lines dropped rather than sent: an empty string is not a date and
    // the server would rightly refuse the lot for one stray newline.
    skip: skip.split('\n').map((x) => x.trim()).filter(Boolean),
    preview,
  });

  const go = async (preview) => {
    setBusy(true);
    setFailed('');
    setDone('');
    try {
      const report = await post(url, body(preview));
      if (preview) {
        setDrawn(report);
      } else {
        setDrawn(null);
        setDone(`${report.written} fixtures added.`);
      }
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="card stack"
         data-testid=${`generate-${division.replace(/\s+/g, '-')}`}>
      <label>Draw the fixtures</label>
      <p class="sub" style="margin:0">
        Everybody plays everybody. Two Teams out of the same pub are never
        both at home on a night it has no room for.
      </p>
      <div class="row pair">
        <div class="stack">
          <span class="sub">First night</span>
          <input type="date" data-testid="generate-start" value=${start}
                 onInput=${(e) => setStart(e.target.value)} />
        </div>
        <div class="stack">
          <span class="sub">Days between</span>
          <input type="number" min="1" data-testid="generate-every"
                 value=${every} onInput=${(e) => setEvery(e.target.value)} />
        </div>
      </div>
      <label class="row" style="gap:.4rem;align-items:center">
        <input type="checkbox" data-testid="generate-double"
               checked=${double}
               onChange=${(e) => setDouble(e.target.checked)} />
        <span class="sub" style="text-transform:none">Home and away</span>
      </label>
      <div class="stack">
        <span class="sub">Nights to skip — one date a line</span>
        <textarea rows="2" data-testid="generate-skip" value=${skip}
                  placeholder="2027-12-22"
                  onInput=${(e) => setSkip(e.target.value)}></textarea>
      </div>
      <button class="secondary" data-testid="generate-preview"
              disabled=${busy || !start}
              onClick=${() => go(true)}>
        ${busy ? 'Drawing…' : 'Show me the draw'}
      </button>

      ${drawn && html`
        <div class="stack" data-testid="generate-preview-out">
          <p class="sub" style="margin:0">
            ${`${drawn.fixtures.length} fixtures over ${drawn.nights.length} `}
            ${drawn.nights.length === 1 ? 'night' : 'nights'}
            ${drawn.nights.length ? `, ${drawn.nights[0]} to `
                                    + `${drawn.nights[drawn.nights.length - 1]}`
                                  : ''}.
          </p>
          ${drawn.problems.length > 0 && html`
            <div class="stack" data-testid="generate-problems">
              <span class="sub" style="text-transform:none">
                ${`${drawn.problems.length} `}
                ${drawn.problems.length === 1 ? 'fixture has' : 'fixtures have'}
                ${' '}nowhere to go. They are not added — the rest are.
              </span>
              ${drawn.problems.map((f, i) => html`
                <p class="sub" key=${i} style="margin:0;text-transform:none">
                  ${`${f.played_on}: ${f.home_name} v ${f.away_name} — ${f.why}`}
                </p>`)}
            </div>`}
          ${drawn.fixtures.slice(0, 8).map((f, i) => html`
            <p class="sub" key=${i} style="margin:0;text-transform:none">
              ${`${f.played_on}: ${f.home_name} v ${f.away_name}`}
              ${f.swapped ? ' (sides swapped for the room)' : ''}
            </p>`)}
          ${drawn.fixtures.length > 8 && html`
            <p class="sub" style="margin:0">
              ${`…and ${drawn.fixtures.length - 8} more.`}
            </p>`}
          <button data-testid="generate-commit" disabled=${busy}
                  onClick=${() => go(false)}>
            ${`Add these ${drawn.fixtures.length} fixtures`}
          </button>
        </div>`}

      ${done && html`<p class="sub" data-testid="generate-done">${done}</p>`}
      ${failed && html`<p class="error" data-testid="generate-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * The card: what a fixture in this league is made of.
 *
 * TWO CONTROLS OVER ONE CARD, and they are not rivals. The counts BUILD a
 * card — "some singles, then some doubles" is what nearly every league plays,
 * BDIPL is six and three, and it is two numbers rather than nine rows. The
 * list below them IS the card: it says what each frame is, in order, and it
 * is what gets saved.
 *
 * Issue #39 asked for "almost unlimited match formats". The model always
 * allowed them — a slot is a frame number and a group, in any arrangement —
 * and it was this form that could only build one shape. A league that plays
 * doubles in the middle can now say so without an API call.
 *
 * `players_per_side` is derived server-side from the group and must not be
 * sent — `LC.normalise` in leaguecard.py works it out itself.
 *
 * Only new fixtures pick up a change: `add_fixture` takes a COPY of the
 * card, so one already scheduled keeps the shape it was given. Said here
 * rather than assumed, since it is the one thing about this control that is
 * not obvious from using it.
 */
function CardEditor({ leagueId, league }) {
  const base = `/api/leagues/${encodeURIComponent(leagueId)}/card`;
  const current = league.card || [];
  const currentSingles = current.filter((s) => s.group === 'Singles').length;
  const currentDoubles = current.filter((s) => s.group === 'Doubles').length;
  const [singles, setSingles] = useState(String(currentSingles));
  const [doubles, setDoubles] = useState(String(currentDoubles));
  // The card being edited, as a list of groups in playing order. This is
  // what gets saved; the counts above are a way to fill it quickly.
  const [order, setOrder] = useState(current.map((slot) => slot.group));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const rebuild = (s, d) => {
    const built = [];
    for (let n = 0; n < Math.max(0, parseInt(s, 10) || 0); n += 1) {
      built.push('Singles');
    }
    for (let n = 0; n < Math.max(0, parseInt(d, 10) || 0); n += 1) {
      built.push('Doubles');
    }
    setOrder(built);
  };

  const at = (i, group) => setOrder(order.map((g, n) => (n === i ? group : g)));
  const drop = (i) => setOrder(order.filter((_, n) => n !== i));

  const save = async () => {
    // frame_no is the position, always 1..n and never a gap: `LC.normalise`
    // refuses a card whose frames are not numbered from one, and a list the
    // committee has just reordered has no other numbering to offer.
    const slots = order.map((group, n) => ({ frame_no: n + 1, group }));
    setBusy(true);
    setFailed('');
    try {
      await put(base, { slots });
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="card stack" data-testid="league-card-room">
      <label>The card</label>
      <p class="sub" style="margin:0">
        What a fixture here is made of: singles first, then doubles. Changing
        this only reaches fixtures made from now on — one already scheduled
        keeps the card it was given.
      </p>
      <div class="row pair">
        <div class="stack">
          <span class="sub">Singles</span>
          <input type="number" min="0" data-testid="card-singles"
                 value=${singles}
                 onInput=${(e) => { setSingles(e.target.value);
                                    rebuild(e.target.value, doubles); }} />
        </div>
        <div class="stack">
          <span class="sub">Doubles</span>
          <input type="number" min="0" data-testid="card-doubles"
                 value=${doubles}
                 onInput=${(e) => { setDoubles(e.target.value);
                                    rebuild(singles, e.target.value); }} />
        </div>
      </div>
      ${/* The card itself, frame by frame. Shown under the counts because
            it is the thing being saved -- the numbers above only fill it. */''}
      <div class="stack" data-testid="card-order">
        ${order.map((group, i) => html`
          <div class="row pair" key=${i}>
            <span class="sub" style="text-transform:none">
              ${`Frame ${i + 1}`}
            </span>
            <select class="combo" data-testid=${`card-frame-${i + 1}`}
                    value=${group}
                    onChange=${(e) => at(i, e.target.value)}>
              <option value="Singles">Singles</option>
              <option value="Doubles">Doubles</option>
            </select>
            <button class="secondary" style="width:auto"
                    data-testid=${`card-drop-${i + 1}`}
                    onClick=${() => drop(i)}>Take out</button>
          </div>`)}
        <button class="secondary" data-testid="card-add"
                onClick=${() => setOrder([...order, 'Singles'])}>
          Add a frame
        </button>
      </div>
      <button data-testid="card-save" disabled=${busy}
              onClick=${save}>${busy ? 'Saving…' : 'Save the card'}</button>
      <p class="sub" data-testid="card-current" style="margin:0">
        ${`Now: ${currentSingles} singles, ${currentDoubles} doubles.`}
      </p>
      ${failed && html`<p class="error" data-testid="card-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * The points rule: how a result becomes points, and what breaks a tie.
 *
 * The four numbers are offered as numbers — BDIPL's default is a point a
 * frame and nothing for the fixture itself. `order` is left exactly as
 * stored rather than given a free-form list to mistype: it is a closed
 * vocabulary the server already refuses an unknown key from, and getting it
 * genuinely clearer than "leave it alone" is a screen of its own, not a
 * field bolted on here.
 */
function PointsEditor({ leagueId, league }) {
  const base = `/api/leagues/${encodeURIComponent(leagueId)}/points`;
  const rule = league.points || {};
  const [frame, setFrame] = useState(String(rule.frame_points));
  const [win, setWin] = useState(String(rule.win_points));
  const [draw, setDraw] = useState(String(rule.draw_points));
  const [loss, setLoss] = useState(String(rule.loss_points));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const save = async () => {
    setBusy(true);
    setFailed('');
    try {
      await put(base, {
        frame_points: Number(frame) || 0,
        win_points: Number(win) || 0,
        draw_points: Number(draw) || 0,
        loss_points: Number(loss) || 0,
        order: rule.order,
      });
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const field = (label, value, onChange, testid) => html`
    <div class="stack">
      <span class="sub">${label}</span>
      <input type="number" step="any" data-testid=${testid} value=${value}
             onInput=${(e) => onChange(e.target.value)} />
    </div>`;

  return html`
    <div class="card stack" data-testid="league-points-room">
      <label>Points</label>
      <p class="sub" style="margin:0">
        How a result in this league becomes points in the table.
      </p>
      ${field('Points a frame', frame, setFrame, 'points-frame')}
      ${field('Points for winning the fixture', win, setWin, 'points-win')}
      ${field('Points for a draw', draw, setDraw, 'points-draw')}
      ${field('Points for losing', loss, setLoss, 'points-loss')}
      <button data-testid="points-save" disabled=${busy}
              onClick=${save}>
        ${busy ? 'Saving…' : 'Save the points rule'}
      </button>
      <p class="sub" data-testid="points-current" style="margin:0">
        ${`Now: ${fig(rule.frame_points)} a frame, `}
        ${`${fig(rule.win_points)} for a win, `}
        ${`${fig(rule.draw_points)} for a draw, `}
        ${`${fig(rule.loss_points)} for a loss.`}
      </p>
      ${failed && html`<p class="error" data-testid="points-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Standings: the table, as the league itself works it out.
 *
 * Copied rather than recomputed. BDIPL awards a point a frame and its own
 * tie-breaks; a table worked out here from the frames would sooner or later
 * disagree with the one on the wall of the pub, and the one on the wall is the
 * one that counts.
 */
function Standings({ leagueId, league, onGo }) {
  const { loaded, seasons, season, division, seasonPicker,
          divisionPicker } = useSeason(leagueId);
  const ready = season && division;
  const [rows, error] = useRead(ready
    ? `/api/leagues/${encodeURIComponent(leagueId)}/table`
      + `?season=${encodeURIComponent(season)}`
      + `&division=${encodeURIComponent(division)}`
    : null);

  if (!loaded) return html`<p class="sub">Loading…</p>`;

  // Nothing to ask the endpoint about yet — no season, or a season with no
  // division in it — and saying so is better than an empty grid, or worse,
  // "Loading…" for ever: `ready` never becomes true, so nothing would ever
  // arrive to replace it. Both kinds of league can be here now: a league run
  // here starts with no season open any more than a mirrored one starts
  // without an archive behind it, and a native league's table is as real as
  // a mirrored one's once there is a season and a division to read it for.
  if (!ready) {
    return html`
      <p class="empty" data-testid="standings-none">
        No table yet. ${seasons.length === 0
          ? (league.source === 'cueforge'
            ? 'No season has been opened for this league yet.'
            : 'This league has no seasons yet.')
          : 'This season has no division to show a table for yet.'}
      </p>`;
  }

  return html`
    <div class="stack" data-testid="league-standings">
      <div class="stack" data-testid="league-picker">
        ${seasonPicker}
        ${divisionPicker(false)}
      </div>
      ${error
        ? html`<p class="error">${error.detail || error.message}</p>`
        : rows === undefined
          ? html`<p class="sub">Loading…</p>`
          : rows.length === 0
            ? html`<p class="empty" data-testid="standings-empty">
                     Nothing in this table yet.
                   </p>`
            : html`
              <div class="tablewrap">
                <table class="league" data-testid="standings-table">
                  <thead>
                    <tr>
                      <th class="num">#</th><th>Team</th>
                      <th class="num">P</th><th class="num">W</th>
                      <th class="num">L</th><th class="num">F</th>
                      <th class="num">A</th><th class="num">Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rows.map((r) => html`
                      <tr key=${r.team_key}
                          data-testid=${`standing-${r.position}`}>
                        <td class="num sub">${r.position}</td>
                        <td>
                          ${/* Pressable only once somebody has said which
                                circle it is. Until then it is a name in
                                another league's book and leads nowhere. */''}
                          ${r.circle_id
                            ? html`<button class="linky"
                                     data-testid=${`standing-go-${r.position}`}
                                     onClick=${() => onGo('team', leagueId,
                                                          r.circle_id)}>
                                     ${r.name}
                                   </button>`
                            : r.name}
                        </td>
                        <td class="num">${r.played}</td>
                        <td class="num">${r.won}</td>
                        <td class="num">${r.lost}</td>
                        <td class="num sub">${fig(r.for)}</td>
                        <td class="num sub">${fig(r.against)}</td>
                        <td class="num pts">${fig(r.points)}</td>
                      </tr>`)}
                  </tbody>
                </table>
              </div>`}
    </div>`;
}

/** Whole numbers where the league keeps whole numbers. 164, not 164.0. */
function fig(n) {
  if (n === null || n === undefined) return '';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

/**
 * Competitions: the cups, and who has won each one.
 *
 * `/api/leagues/{id}/competitions` (see cueforge/api.py's
 * `league_competitions`) already carries every competition instance this
 * league has run, finished or not — this only has to lay it out. A
 * finished instance names its winner; one still to come says so rather
 * than a blank line, since a blank line here reads as "the data is
 * missing" and not as "nobody has won yet".
 */
function Competitions({ leagueId, onGo }) {
  const [rows, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/competitions`);

  if (error) {
    return html`<p class="error">${error.detail || error.message}</p>`;
  }
  if (rows === undefined) return html`<p class="sub">Loading…</p>`;
  if (rows.length === 0) {
    return html`
      <p class="empty" data-testid="cups-none">
        No competitions recorded for this league yet.
      </p>`;
  }

  return html`
    <div class="stack" data-testid="league-cups">
      ${/* Pressable since issue #30: each one opens its own page, with who
            entered and the knockout as it was played. A button rather than
            a card, because it now leads somewhere. */''}
      ${rows.map((c) => html`
        <button class="secondary card rowbetween cuprow"
             key=${`${c.season}-${c.competition}`}
             data-testid=${`cup-${slug(c.season)}-${slug(c.competition)}`}
             onClick=${() => onGo('cup', leagueId, c.season, c.competition)}>
          ${/* Issue #34: the name hard left, the winner hard right, so the
                eye has an edge to run down. Both ends are anchored by
                `.rowbetween` now, and `.cuprow` (issue #45) makes the NAME
                the side that wraps -- left to themselves both ends may
                shrink, and the browser chose the short one, breaking "Not
                finished yet" in half beside a name it left on one line. */''}
          <span>
            <span style="display:block">${c.competition}</span>
            <span class="sub">${c.season}</span>
          </span>
          ${c.entrant
            ? html`<span class="sub"
                     style="text-align:right;text-transform:none"
                     data-testid=${`cup-winner-${
                     slug(c.season)}-${slug(c.competition)}`}>
                     ${c.entrant}
                   </span>`
            : html`<span class="sub" data-testid=${`cup-unfinished-${
                     slug(c.season)}-${slug(c.competition)}`}>
                     Not finished yet
                   </span>`}
        </button>`)}
    </div>`;
}

/** A value stable enough for a `data-testid`, out of a name with spaces
 * and slashes in it — the same trick `season-${s.replace(/\s+/g, '-')}`
 * uses elsewhere in this file, extended to `/` since a pair honour's
 * competition name can carry one too. */
function slug(s) {
  return String(s).replace(/[\s/]+/g, '-');
}

/**
 * Stats: the league's own rating board.
 *
 * The league's book, not the friendlies one. They are shown side by side
 * elsewhere and never merged — a league's book is reproducible from that
 * league's own records, and this app's friendlies are not part of them.
 */
function Stats({ leagueId, onGo }) {
  const { loaded, seasons, season, seasonPicker } = useSeason(leagueId);
  // Issue #41: singles and doubles boards, not just the combined one. The
  // league keeps three books and always has -- `league_rating.kind` -- and
  // the endpoint has taken `kind` since it was written; this screen simply
  // never asked for anything but the default.
  //
  // A REFETCH, not a filter. Singles and doubles ratings are computed from
  // their own frames, so a doubles board is not the combined board with the
  // singles rows hidden -- the numbers themselves differ.
  const [kind, setKind] = useState('combined');
  // Which number the last column shows. Issue #41 asked for a break-and-dish
  // rank and a reverse-dish rank, and this is them: the column SWAPS rather
  // than being added, because the board is deliberately six columns wide so
  // a phone shows all of it (that column was reported off the edge once
  // already, and two more would put it back there).
  //
  // The league counts singles and doubles dishes separately and publishes no
  // combined figure, so these follow the board you are on -- and adding them
  // up ourselves would invent a number the league does not keep.
  const [metric, setMetric] = useState('elo');
  const dishKey = (what) => (kind === 'combined' ? null
    : `${kind}_${what === 'bd' ? 'break_and_dish' : 'reverse_dish'}`);
  const metricKey = metric === 'elo' ? 'elo' : dishKey(metric);
  const metricLabel = metric === 'elo' ? 'Rating'
    : metric === 'bd' ? 'B&D' : 'Rev';
  const [rows, error] = useRead(season
    ? `/api/leagues/${encodeURIComponent(leagueId)}/ratings`
      + `?season=${encodeURIComponent(season)}`
      + `&kind=${encodeURIComponent(kind)}`
    : null);

  // A search box, a set of filters and sortable columns, all over rows
  // already fetched — the whole season arrives in one request (see
  // `league_ratings`'s own docstring on why it is not paginated), so there
  // is nothing here worth asking the server to do again. Filters narrow the
  // same `rows` the search box and the sort already work over, so all three
  // compose: whichever runs first, the other two still see only what it let
  // through (issue #6).
  const [query, setQuery] = useState('');
  // Which column, and which way. Position ascending is what the page shows
  // today, so it stays the default.
  const [sort, setSort] = useState({ by: 'position', desc: false });

  // A SET of divisions, not one (issue #41): "Division 2 and Division 3" is
  // a thing a committee wants to look at, and picking them one at a time
  // cannot express it. Empty means every division -- the off state, without a
  // separate flag.
  const [picked, setPicked] = useState(() => new Set());
  const toggleDivision = (d) => setPicked((was) => {
    const next = new Set(was);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });
  const emptyRange = () => ({ games: { min: '', max: '' },
                               won: { min: '', max: '' },
                               win_pct: { min: '', max: '' },
                               elo: { min: '', max: '' } });
  const [range, setRange] = useState(emptyRange());
  const setBound = (key, edge, v) => setRange((r) => (
    { ...r, [key]: { ...r[key], [edge]: v } }));

  // The columns a range filter is offered on: Frames, Wins, Win% and Rating
  // are all the same shape (a number, sometimes unknown -- see `_win_record`
  // in cueforge/api.py), so one field-list drives both the filtering below
  // and the inputs rendered further down rather than four hand-written
  // copies of each.
  const RANGE_FIELDS = [
    { key: 'games', label: 'Frames' },
    { key: 'won', label: 'Wins' },
    { key: 'win_pct', label: 'Win%' },
    { key: 'elo', label: 'Rating' },
  ];
  const rangeActive = ({ min, max }) => min !== '' || max !== '';
  // GitHub issue #48: the four min/max pairs fold away behind one summary.
  // Eight number boxes were the tallest thing on the screen and the thing
  // asked for least often -- the search box, the board and the division
  // chips were all below them, so the common case scrolled past the rare one.
  const anyRangeActive = RANGE_FIELDS.some(({ key }) => rangeActive(range[key]));
  const anyFilterActive = picked.size > 0 || kind !== 'combined'
    || anyRangeActive;

  // The set to pick a division from is the divisions THIS season's rows
  // actually carry, not the narrowed `shown` list -- switching one filter
  // must never make another filter's own options shrink out from under it.
  const divisions = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map((r) => r.division).filter(Boolean))].sort();
  }, [rows]);

  const shown = useMemo(() => {
    if (!rows) return [];
    // Always a fresh array, even when nothing below actually filters --
    // `.sort()` mutates in place, and sorting `rows` itself would leave the
    // NEXT render's "no filter" case reading an order the sort left behind.
    let kept = rows.slice();
    if (picked.size) kept = kept.filter((r) => picked.has(r.division));
    // A range with no bound set does not filter at all; a bound is
    // inclusive at both ends (min <= value <= max), so typing a player's
    // own number into either box keeps them on the board rather than
    // bumping them off it. A row whose value is unknown (`won`/`win_pct`
    // can be NULL -- see `_win_record`) fails any bound that IS set: it
    // is not a low score, so it cannot be said to be inside a range either.
    for (const { key } of RANGE_FIELDS) {
      const { min, max } = range[key];
      if (min === '' && max === '') continue;
      kept = kept.filter((r) => {
        const v = r[key];
        if (v === null || v === undefined || v === '') return false;
        if (min !== '' && v < Number(min)) return false;
        if (max !== '' && v > Number(max)) return false;
        return true;
      });
    }
    const needle = query.trim().toLowerCase();
    if (needle) {
      kept = kept.filter((r) => (r.name || '').toLowerCase().includes(needle));
    }
    // An unknown W/L is not a low score -- it is not a score. It sorts to
    // the bottom whichever way the arrow points, so reversing never floats
    // "we do not know" to the top of the board. An empty string counts as
    // unknown too, the same as null/undefined -- a name that is merely
    // missing should not sort ahead of one that is explicitly not-known-yet,
    // and it should not float to the top ascending the way an ordinary
    // string comparison would put "" before every letter. And when BOTH
    // sides are unknown, that is a tie, not an order: returning 1 (as this
    // used to) made cmp(a,b) and cmp(b,a) both positive for the same pair,
    // which is not a valid comparator, so this returns 0 and lets the
    // position tie-break below settle it via the sort's stability instead.
    const val = (r) => r[sort.by];
    kept.sort((a, b) => {
      const x = val(a); const y = val(b);
      const xUnknown = x === null || x === undefined || x === '';
      const yUnknown = y === null || y === undefined || y === '';
      if (xUnknown && yUnknown) return 0;
      if (xUnknown) return 1;
      if (yUnknown) return -1;
      if (x === y) return (a.position || 0) - (b.position || 0);
      const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.desc ? -cmp : cmp;
    });
    return kept;
  }, [rows, query, sort, picked, range]);

  if (!loaded) return html`<p class="sub">Loading…</p>`;
  if (seasons.length === 0) {
    return html`
      <p class="empty" data-testid="stats-none">
        No ratings yet. They are worked out from the frames, so there is
        nothing until a fixture has been played.
      </p>`;
  }

  // Not "0" -- that reads as a fact about a real person. `won`/`lost`/
  // `win_pct` arrive NULL together for a row that predates the `won`
  // column (see cueforge/api.py `_win_record`), meaning "not computed yet".
  const cell = (v) => (v === null || v === undefined ? '—' : v);
  // Won and lost share one column on a phone-width board (see the CSS on
  // `table.league.stats`) -- each half still goes through `cell()`
  // independently, so an unknown row still shows two dashes here, not one
  // combined "no data" -- `test_a_row_with_unknown_wins_shows_a_dash_and_
  // sorts_last` counts them separately. The separator is an EN dash, never
  // the EM dash `cell()` uses for "unknown", so it can never be mistaken
  // for a third one.
  const wl = (r) => `${cell(r.won)}–${cell(r.lost)}`;
  const arrow = (key) => (sort.by === key ? (sort.desc ? ' ▼' : ' ▲') : '');
  // Same column toggles direction; a new column starts ascending. `r.position`
  // is what `#` always renders -- never the row index -- so this cannot
  // make "3rd" mean something different depending on how the board is
  // sorted.
  const head = (key, label, cls) => html`
    <th class=${cls || ''}>
      <button class="linky" data-testid=${`stats-sort-${key}`}
              onClick=${() => setSort((s) => (s.by === key
                ? { by: key, desc: !s.desc } : { by: key, desc: false }))}>
        ${label}${arrow(key)}
      </button>
    </th>`;

  // One min/max pair per numeric column -- `.row.pair` around two `.stack`s
  // is the same shape `PointsEditor`'s Singles/Doubles fields already use
  // above, not a new pattern. Both inputs share one row so four of these
  // read as one compact block rather than eight separate lines.
  const rangeField = ({ key, label }) => html`
    <div class="row pair" key=${key}>
      <div class="stack">
        <span class="sub">${`Min ${label}`}</span>
        <input type="number" inputmode="decimal" step="any"
               data-testid=${`stats-min-${key}`} value=${range[key].min}
               onInput=${(e) => setBound(key, 'min', e.target.value)} />
      </div>
      <div class="stack">
        <span class="sub">${`Max ${label}`}</span>
        <input type="number" inputmode="decimal" step="any"
               data-testid=${`stats-max-${key}`} value=${range[key].max}
               onInput=${(e) => setBound(key, 'max', e.target.value)} />
      </div>
    </div>`;

  // Only offered once there is more than one division to tell apart --
  // the same threshold `useSeason`'s own division nav uses, for the same
  // reason: a single-option picker is not a choice.
  const divisionPicker = divisions.length > 1 && html`
    <div class="seg" data-testid="stats-divisions">
      ${/* Each division toggles, so "Division 2 and Division 3" is
            expressible. "All" is the empty set rather than a button that
            competes with the others for being pressed. */''}
      <button aria-pressed=${picked.size === 0} data-testid="stats-division-all"
              onClick=${() => setPicked(new Set())}>All</button>
      ${divisions.map((d) => html`
        <button key=${d} aria-pressed=${picked.has(d)}
                data-testid=${`stats-division-${d.replace(/\s+/g, '-')}`}
                onClick=${() => toggleDivision(d)}>${d}</button>`)}
    </div>`;

  // Three books, not three views of one.
  const kindPicker = html`
    <div class="seg" data-testid="stats-kinds">
      ${[['combined', 'Combined'], ['singles', 'Singles'],
         ['doubles', 'Doubles']].map(([k, label]) => html`
        <button key=${k} aria-pressed=${kind === k}
                data-testid=${`stats-kind-${k}`}
                onClick=${() => { setKind(k);
                                  // There is no combined dish figure, so a
                                  // dish column cannot survive the move.
                                  if (k === 'combined') setMetric('elo');
                                  if (sort.by !== 'position') {
                                    setSort({ by: 'position', desc: false });
                                  } }}>${label}</button>`)}
    </div>`;

  // Only where the league has a number: it counts singles and doubles dishes
  // separately and publishes no combined one.
  const metricPicker = kind !== 'combined' && html`
    <div class="seg" data-testid="stats-metrics">
      ${[['elo', 'Rating'], ['bd', 'Break & dish'],
         ['rd', 'Reverse dish']].map(([m, label]) => html`
        <button key=${m} aria-pressed=${metric === m}
                data-testid=${`stats-metric-${m}`}
                onClick=${() => { setMetric(m);
                                  const key = m === 'elo' ? 'elo'
                                    : `${kind}_${m === 'bd'
                                        ? 'break_and_dish' : 'reverse_dish'}`;
                                  setSort({ by: key, desc: true }); }}>
          ${label}
        </button>`)}
    </div>`;

  return html`
    <div class="stack" data-testid="league-stats">
      ${seasonPicker}
      ${error
        ? html`<p class="error">${error.detail || error.message}</p>`
        : rows === undefined
          ? html`<p class="sub">Loading…</p>`
          : rows.length === 0
            ? html`<p class="empty" data-testid="stats-empty">
                     Nothing played in this season yet.
                   </p>`
            : html`
              <div class="stack" data-testid="stats-filters">
                <input class="combo" data-testid="stats-search" type="search"
                       placeholder="Find a player" value=${query}
                       onInput=${(e) => setQuery(e.target.value)} />
                ${kindPicker}
                ${metricPicker}
                ${divisionPicker}
                ${/* Issue #48. Native <details>, so it is keyboard-reachable
                      and carries its own open state.

                      THE SUMMARY COUNTS WHAT IS SET, and that is the part
                      that matters. A board narrowed by a filter folded out of
                      sight is a screen showing the wrong rows for no visible
                      reason -- worse than the eight boxes this replaced,
                      because at least those said what they were doing. The
                      first attempt tried to hold the section OPEN whenever a
                      range was live, and it does not work: Preact only
                      re-applies `open` when the value it diffs has changed,
                      so once somebody collapses it by hand a live filter
                      cannot push it back. Forcing it would be fighting the
                      person using it anyway. Saying so on the closed summary
                      is better than both -- collapse it all you like, it
                      still tells you it is doing something.

                      `open` on first render only, as a hint, for the case
                      where a filter is somehow already set when the screen
                      appears. */''}
                <details class="advanced" data-testid="stats-advanced"
                         open=${anyRangeActive}>
                  <summary data-testid="stats-advanced-toggle">
                    <span>Advanced filters</span>
                    ${anyRangeActive && html`
                      <span class="sub" data-testid="stats-advanced-count">
                        ${`${RANGE_FIELDS.filter(({ key }) =>
                             rangeActive(range[key])).length} set`}
                      </span>`}
                  </summary>
                  <div class="stack">${RANGE_FIELDS.map(rangeField)}</div>
                </details>
                ${anyFilterActive && html`
                  <button class="secondary" style="width:auto"
                          data-testid="stats-clear-filters"
                          onClick=${() => { setPicked(new Set());
                                             setKind('combined');
                                             setRange(emptyRange()); }}>
                    Clear filters
                  </button>`}
              </div>`}
      ${!error && rows && rows.length > 0
        ? (shown.length === 0
          ? html`
            <p class="empty" data-testid="stats-empty-search">
              ${query.trim()
                ? 'No player by that name in this season.'
                : 'No player matches these filters.'}
            </p>`
          : html`
            <div class="tablewrap">
              ${/* Six columns, not eight -- Division moved under the
                    player's name and Won/Lost share one column (`wl`
                    above) -- so a phone shows every column with no
                    sideways scroll. Nothing here is gone: Division is
                    still text on the row, and `wl` still reads each of
                    Won and Lost through `cell()` on its own. */''}
              <table class="league stats" data-testid="stats-table">
                <thead>
                  <tr>
                    ${head('position', '#', 'num')}
                    ${head('name', 'Player')}
                    ${head('games', 'Frames', 'num')}
                    ${/* Two orders, one column (issue #41): "ordered by
                          wins" and "ordered by losses" are different
                          questions and the board could only answer the
                          first. `lost` is a column on the row already. */''}
                    <th class="num">
                      <button class="linky" data-testid="stats-sort-won"
                              onClick=${() => setSort((x) => (x.by === 'won'
                                ? { by: 'won', desc: !x.desc }
                                : { by: 'won', desc: true }))}>
                        ${`W${arrow('won')}`}
                      </button>
                      <span class="sub">-</span>
                      <button class="linky" data-testid="stats-sort-lost"
                              onClick=${() => setSort((x) => (x.by === 'lost'
                                ? { by: 'lost', desc: !x.desc }
                                : { by: 'lost', desc: true }))}>
                        ${`L${arrow('lost')}`}
                      </button>
                    </th>
                    ${head('win_pct', 'Win%', 'num')}
                    ${head(metricKey || 'elo', metricLabel, 'num')}
                  </tr>
                </thead>
                <tbody>
                  ${shown.map((r) => html`
                    <tr key=${r.player_key}
                        data-testid=${`stat-${r.player_key}`}>
                      <td class="num sub">${r.position}</td>
                      <td>
                        ${/* The one control on the board that leads
                              somewhere -- a player's own page, with their
                              all-time book and this season's alongside it. */''}
                        <button class="linky"
                                data-testid=${`stat-go-${r.player_key}`}
                                onClick=${() => onGo('player', leagueId,
                                                     r.player_key)}>
                          ${r.name}
                        </button>
                        ${/* Division, not dropped -- just no longer its
                              own column. Same two-line cell the team list
                              above uses for name-plus-venue. */''}
                        <span class="sub" style="display:block"
                              >${r.division}</span>
                      </td>
                      <td class="num sub">${r.games}</td>
                      <td class="num">${wl(r)}</td>
                      <td class="num">${cell(r.win_pct)}</td>
                      ${/* The rating, or whichever count the picker put in
                            its place. `cell()` because a dish count is
                            genuinely unknown for a player the league's board
                            does not list -- a dash, never a 0, which would
                            claim they went a season without one. */''}
                      <td class="num pts">
                        ${metricKey === 'elo'
                          ? Math.round(r.elo)
                          : cell(r[metricKey])}
                      </td>
                    </tr>`)}
                </tbody>
              </table>
            </div>`)
        : ''}
    </div>`;
}

/**
 * Match Hub: a week at a time, with arrows.
 *
 * A week rather than everything, because a fixture list for a season is four
 * hundred rows and the question being asked is "who are we playing this
 * week". Monday to Monday, since that is how a pub league's week runs.
 */
function MatchHub({ leagueId, league, onGo }) {
  const [offset, setOffset] = useState(0);
  const [whenBox, setWhen] = useState({});
  const [failed, setFailed] = useState('');
  const { anyDivision, divisionPicker, competitionPicker } =
    useSeason(leagueId);
  const from = mondayOf(new Date(), offset);
  const to = mondayOf(new Date(), offset + 1);

  const run = async (fn) => {
    setFailed('');
    try { await fn(); } catch (e) { setFailed(e.detail || e.message); }
  };
  // No season picker here. The arrows already say when, and a season select
  // beside them would be a second control for the same question — one that
  // can disagree with the other, and then a week shows nothing for no visible
  // reason.
  const [fixtures, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/fixtures`
    + `?start=${from.toISOString().slice(0, 10)}`
    + `&end=${to.toISOString().slice(0, 10)}`
    + (anyDivision ? `&division=${encodeURIComponent(anyDivision)}` : ''));

  return html`
    <div class="stack" data-testid="league-fixtures">
      ${/* Four divisions play the same night, so a week is twenty-odd rows
            and the one you want is somebody else's. */''}
      ${divisionPicker(true)}
      ${/* And the season's knockouts, which were unreachable here (issue
            #26) -- not because the filter could not express them, but
            because nothing offered their names. `All` above clears these
            too: it is the same one filter, so there is never a division and
            a cup selected at once, which would be a question with no
            answer. */''}
      ${competitionPicker()}

      <div class="rowbetween">
        <button class="secondary" style="width:auto"
                data-testid="hub-back" onClick=${() => setOffset(offset - 1)}>
          ←
        </button>
        <span class="mono" data-testid="hub-week">
          ${offset === 0 ? 'This week' : week(from)}
        </span>
        <button class="secondary" style="width:auto"
                data-testid="hub-next" onClick=${() => setOffset(offset + 1)}>
          →
        </button>
      </div>

      ${error
        ? html`<p class="error">${error.detail || error.message}</p>`
        : fixtures === undefined
          ? html`<p class="sub">Loading…</p>`
          : fixtures.length === 0
            ? html`<p class="empty" data-testid="hub-none">
                     Nothing on this week.
                   </p>`
            : fixtures.map((f) => html`
                <div class="card stack" key=${f.fixture_id}
                     data-testid=${`hub-fixture-${f.fixture_id}`}>
                  ${/* Pressable whatever the league's source -- issue #74.
                        This was gated on a league run HERE, and the gate was
                        right when it was written: a mirrored fixture had no
                        screen to open. Issue #35 gave it one a week later --
                        read-only, showing the card the archive holds and
                        pointing back at the league's own page for it -- and
                        nobody came back to lift the gate, so every row in
                        BDIPL's Match Hub was dead. The scores were also the
                        same markup twice, once per branch, which is a second
                        copy to forget: there is one now. */''}
                  ${/* `hubrow`, not `rowbetween` -- issue #86. `space-between`
                        positions the two names and lets the score land
                        wherever they leave room, so it sat off-centre and
                        moved from row to row with the length of the names
                        either side. A three-column grid pins it: the outer
                        columns are equal, so the middle one is the middle of
                        the card whatever is in them, and a long name wraps
                        inside its own half instead of shoving the score
                        along. */''}
                  <button class="linky hubrow" style="width:100%"
                          data-testid=${`hub-open-${f.fixture_id}`}
                          onClick=${() => onGo('fixture', leagueId,
                                               f.fixture_id)}>
                    <span>${f.home.name}</span>
                    ${/* The score where there is one, "v" where there is
                          not. Never 0-0 for an unplayed match: a fixture
                          that has not happened is not a draw. */''}
                    ${f.score
                      ? html`<span class="mono pts"
                                   data-testid=${`hub-score-${
                                     f.fixture_id}`}>
                               ${`${f.score.home}–${f.score.away}`}
                             </span>`
                      : html`<span class="mono sub">v</span>`}
                    <span>${f.away.name}</span>
                  </button>
                  <div class="rowbetween">
                    <span class="sub">
                      ${`${when(f.starts_at || f.played_on)}${f.venue
                        ? ` · ${f.venue}` : ''}`}
                      ${f.division ? ` · ${f.division}` : ''}
                      ${f.status === 'postponed' ? ' · Postponed' : ''}
                    </span>
                  </div>

                  ${/* Not offered for a mirrored league. Its calendar is the
                        other league's, read from an archive — a fixture
                        written here would go into our log and never appear,
                        which is worse than no button. */''}
                  ${league.may_fixture && league.source !== 'external' && html`
                    <div class="row pair"
                         data-testid=${`hub-edit-${f.fixture_id}`}>
                      <input type="datetime-local"
                             data-testid=${`hub-when-${f.fixture_id}`}
                             value=${whenBox[f.fixture_id]
                                    ?? (f.starts_at || '').slice(0, 16)}
                             onInput=${(e) => setWhen({
                               ...whenBox, [f.fixture_id]: e.target.value })} />
                      <button class="secondary" style="width:auto"
                              data-testid=${`hub-reschedule-${f.fixture_id}`}
                              onClick=${() => run(() => post(
                                `/api/leagues/${encodeURIComponent(leagueId)}`
                                + `/fixtures/${encodeURIComponent(f.fixture_id)}`
                                + `/reschedule`,
                                { starts_at: whenBox[f.fixture_id]
                                             || f.starts_at }))}>Move it</button>
                      <button class="secondary" style="width:auto"
                              data-testid=${`hub-postpone-${f.fixture_id}`}
                              onClick=${() => run(() => post(
                                `/api/leagues/${encodeURIComponent(leagueId)}`
                                + `/fixtures/${encodeURIComponent(f.fixture_id)}`
                                + `/postpone`, {}))}>Call it off</button>
                      <button class="secondary" style="width:auto"
                              data-testid=${`hub-drop-${f.fixture_id}`}
                              onClick=${() => run(() => del(
                                `/api/leagues/${encodeURIComponent(leagueId)}`
                                + `/fixtures/${encodeURIComponent(f.fixture_id)}`
                              ))}>Take it out</button>
                    </div>`}
                </div>`)}

      ${failed && html`<p class="error" data-testid="hub-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * Putting one in the calendar.
 *
 * A season and a division are not optional here, whatever the endpoint's own
 * defaults say: `league_standing` in leagueproject.py works out a table per
 * (season, division), and a fixture with neither matches no table and is
 * silently skipped — created, and then invisible, with nothing to say why.
 * So this asks its own `useSeason`, independent of the Match Hub's — picking
 * a division to schedule INTO is a different question from which division's
 * week is on screen — and the control cannot be submitted without both.
 *
 * The Teams offered are only the ones the chosen division actually has —
 * `seasons[].teams` from `GET /seasons`, not `league.teams`, which is every
 * Team in the league regardless of division and would let this pick a
 * fixture the server has to refuse.
 *
 * A time, not just a day: `starts_at` is what a fixture is scheduled by, and
 * "Thursday" is not a fixture — "8pm Thursday" is.
 */
function AddFixture({ leagueId }) {
  const { loaded, seasons, season, division, seasonPicker, divisionPicker } =
    useSeason(leagueId);
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  // What was just written. The form used to sit directly above the Match
  // Hub's own week, so a fixture appearing in the list WAS the confirmation.
  // Moving the form under Committee (issue #13) separated the two and left
  // the press with no visible effect at all -- the fields clear, and that is
  // equally what a silently failed request looks like.
  const [wrote, setWrote] = useState(null);

  const add = async () => {
    setBusy(true);
    setFailed('');
    try {
      const teamName = (id) => (teams.find((t) => t.circle_id === id) || {}).name;
      await post(`/api/leagues/${encodeURIComponent(leagueId)}/fixtures`,
                 { home, away, season, division, starts_at: when });
      setWrote({ home: teamName(home), away: teamName(away), when });
      setHome(''); setAway(''); setWhen('');
    } catch (e) {
      setFailed(e.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!loaded) return html`<p class="sub">Loading…</p>`;

  // Nowhere to put it: no season open, or a season with no division in
  // it. Offering the form anyway would let it be submitted with neither,
  // which is the exact bug this screen exists to close.
  if (seasons.length === 0 || !division) {
    return html`
      <p class="sub" data-testid="hub-no-season">
        There is no season and division to put a fixture in yet. Open one
        under Season, on this page.
      </p>`;
  }

  const inSeason = seasons.find((s) => s.season === season) || {};
  const teams = (inSeason.teams || {})[division] || [];

  if (teams.length < 2) {
    return html`
      <div class="stack" data-testid="hub-add">
        ${seasonPicker}
        ${divisionPicker(false)}
        <p class="sub" data-testid="hub-no-teams">
          ${`Two Teams have to be placed in ${division} before there is a `}
          fixture to write.
        </p>
      </div>`;
  }

  const pick = (value, onPick, other, testid, blank) => html`
    <select class="combo" data-testid=${testid} value=${value}
            onChange=${(e) => onPick(e.target.value)}>
      <option value="">${blank}</option>
      ${teams.filter((t) => t.circle_id !== other).map((t) => html`
        <option key=${t.circle_id} value=${t.circle_id}>${t.name}</option>`)}
    </select>`;

  return html`
    <div class="card stack" data-testid="hub-add">
      <label>Put a fixture in</label>
      ${seasonPicker}
      ${divisionPicker(false)}
      ${pick(home, setHome, away, 'hub-home', 'At home')}
      ${pick(away, setAway, home, 'hub-away', 'Away')}
      <input type="datetime-local" data-testid="hub-date" value=${when}
             onInput=${(e) => setWhen(e.target.value)} />
      <button data-testid="hub-add-go"
              disabled=${busy || !home || !away || !when}
              onClick=${add}>${busy ? 'Adding…' : 'Add it'}</button>
      ${failed && html`<p class="error" data-testid="hub-add-error">
                         ${failed}
                       </p>`}
      ${wrote && html`
        <p class="sub" data-testid="hub-add-wrote">
          ${`Written: ${wrote.home} v ${wrote.away}, `}
          ${wrote.when.replace('T', ' ')}
        </p>`}
    </div>`;
}

/** The Monday of the week `n` weeks from today. A pub league's week. */
function mondayOf(from, n) {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + n * 7);
  return d;
}

function week(d) {
  return d.toLocaleDateString(undefined,
    { day: 'numeric', month: 'short' });
}

function day(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined,
    { weekday: 'short', day: 'numeric', month: 'short' });
}

/** A fixture's day and time. The time is the point of having one.
 *
 * `hour12: false` rather than leaving it to the browser's locale: the clock
 * is 24-hour so a test (and a person) can find "20:00" in the rendered text
 * without it depending on which locale Chromium happens to boot with.
 *
 * `timeZone: 'UTC'` for the same reason, and it is not cosmetic: a pub
 * league has one wall clock, not a timezone. This app's convention is that a
 * zoneless stamp IS UTC — see `LC.EVENING`, which hardcodes `+00:00` for
 * exactly this reason — and the read model makes that explicit too,
 * normalising `starts_at` to a real `...Z` instant (`leagueproject._stamp`).
 * A league run HERE reads its calendar off the fold, though, and the fold
 * echoes back exactly what was typed into a `datetime-local` input: no zone
 * marker at all. `new Date()` treats THAT shape as local time by spec (a
 * date-only string is UTC by the same spec; only the datetime form is
 * ambiguous), which would show the pub's 8 o'clock kick-off as 9 o'clock, or
 * 7, depending on where the machine reading this happens to sit. Stamping a
 * zoneless string 'Z' before parsing makes both shapes agree with the one
 * convention: the digits typed are the digits shown, everywhere, whether
 * they came back through the fold or through the read model.
 */
function when(iso) {
  const zoned = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(zoned);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC',
  });
}

/**
 * One fixture, in one of two tabs.
 *
 * Both tabs are component state, not the router: the fixture route already
 * spends its one spare segment on the fixture id
 * (`#/league/{leagueId}/fixture/{fixtureId}`, where `view` carries it — see
 * router.js), and a tab is not worth reopening that. `tab` defaults to
 * 'home' — Match home asks for no capability of its own, so it is what
 * everybody who can reach this screen sees first.
 *
 * The header, the score card and the "every frame is in" line belong to
 * neither tab, so they stay here rather than being duplicated into both.
 */
// The live band's own cadence — `kiosk.js`'s 10s poll for a wall screen
// nobody is touching is the precedent this mirrors: a spectator's live band
// must not freeze at whatever it was when they opened the screen (I3).
// `live.js` was where the spec first pointed, but it does not poll either —
// `useRead` there is the same subscribe-only read this screen used to be.
const LIVE_BAND_POLL_MS = 10000;


/**
 * A fixture in a league run elsewhere — GitHub issue #35.
 *
 * This used to be a 404. "Its calendar is the other league's" was true and
 * unhelpful: opening a future fixture from a team page said "fixture not
 * found" about a fixture that plainly exists and whose whole card we hold.
 *
 * Read-only by SHAPE rather than by hidden buttons — there is nothing here
 * to press because there is nothing here anybody may do. And it links back
 * to the league's own page for the match, because a mirror should be able to
 * point at the thing it mirrors.
 */
function MirroredFixture({ fixture, onDone }) {
  const score = fixture.score;
  const frames = fixture.frames || [];

  return html`
    <div class="screen" data-testid="fixture-screen">
      <div class="rowbetween">
        <h2 data-testid="fixture-title">
          ${`${fixture.home.name} v ${fixture.away.name}`}
        </h2>
        ${/* Issue #77, wrapped with Back for the reason team.js gives:
              `.rowbetween` expects two children. */''}
        <span class="row" style="width:auto">
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="fixture-back" onClick=${onDone}>Back</button>
        </span>
      </div>

      <p class="sub" data-testid="fixture-when">
        ${[fixture.played_on, fixture.division, fixture.season]
          .filter(Boolean).join(' · ')}
      </p>

      <div class="card rowbetween" data-testid="fixture-score">
        <span style="text-align:left;min-width:0">${fixture.home.name}</span>
        <span class="mono pts">
          ${score ? `${score.home}–${score.away}` : 'to play'}
        </span>
        <span style="text-align:right;min-width:0">${fixture.away.name}</span>
      </div>

      ${/* Absent, not an empty table: a fixture still to be played has no
            frames, and a heading over nothing reads as a page that failed
            rather than as a night that has not happened. */''}
      ${frames.length > 0 && html`
        <div class="stack" data-testid="fixture-frames">
          <label>${`${frames.length} `}
            ${frames.length === 1 ? 'frame' : 'frames'}</label>
          <div class="tablewrap">
            <table class="league" data-testid="fixture-frame-table">
              <thead>
                <tr><th></th><th>Home</th><th>Away</th></tr>
              </thead>
              <tbody>
                ${frames.map((f) => html`
                  <tr key=${`${f.game_group}-${f.frame_no}`}
                      data-testid=${`fixture-frame-${f.game_group}-${f.frame_no}`}>
                    <td class="sub">${f.game_group}</td>
                    ${/* The winning side is the one that is not grey, the
                          same way a result reads everywhere else here. */''}
                    <td class=${f.winner === 'home' ? '' : 'sub'}
                        style="text-transform:none">${f.home}</td>
                    <td class=${f.winner === 'away' ? '' : 'sub'}
                        style="text-transform:none">${f.away}</td>
                  </tr>`)}
              </tbody>
            </table>
          </div>
        </div>`}

      ${fixture.source_url && html`
        <a class="pin" data-testid="fixture-source"
           href=${fixture.source_url} target="_blank" rel="noreferrer">
          <${Icon} name="map-pin" size=${16} />
          <span>This match on the league's own site</span>
        </a>`}
    </div>`;
}

export function FixtureScreen({ leagueId, fixtureId, onDone }) {
  const base = `/api/leagues/${encodeURIComponent(leagueId)}`
    + `/fixtures/${encodeURIComponent(fixtureId)}`;
  const [fixture, error] = useRead(base, LIVE_BAND_POLL_MS);
  const [tab, setTab] = useState('home');
  const [failed, setFailed] = useState('');

  if (error) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="fixture-error">
          ${error.detail || error.message}
        </p>
      </div>`;
  }
  if (fixture === undefined) return html`<p class="sub">Loading…</p>`;

  // A mirrored league's fixture is READ, not played (issue #35). It has no
  // slots, no availability and nothing to pick, because the league it
  // belongs to is run elsewhere -- so it gets its own screen rather than
  // this one with every control hidden. `readonly` comes from the server,
  // which is the only thing that knows whose league this is.
  if (fixture.readonly) {
    return html`<${MirroredFixture} fixture=${fixture} onDone=${onDone} />`;
  }

  // Offered only to somebody who could actually pick for a side — a roster
  // manager for the home Team, the away Team, or both. Everyone else would
  // just find a screen that answers "you may not", which is worse than not
  // offering it.
  const mayPick = fixture.may_select
    && (fixture.may_select.home || fixture.may_select.away);

  return html`
    <div class="screen" data-testid="fixture-screen">
      <div class="rowbetween">
        <h2>${fixture.home.name} v ${fixture.away.name}</h2>
        ${/* Issue #77, the same as the read-only fixture screen above. */''}
        <span class="row" style="width:auto">
          <${KeepPage} variant="plain" />
          <button class="secondary" style="width:auto"
                  data-testid="fixture-back" onClick=${onDone}>Back</button>
        </span>
      </div>

      <p class="sub">
        ${`${when(fixture.starts_at)}${fixture.venue
          ? ` · ${fixture.venue}` : ''}`}
        ${fixture.division ? ` · ${fixture.division}` : ''}
      </p>

      <div class="card rowbetween" data-testid="fixture-score">
        <span>${fixture.home.name}</span>
        <span class="mono pts">
          ${`${fixture.score.home}–${fixture.score.away}`}
        </span>
        <span>${fixture.away.name}</span>
      </div>

      ${fixture.complete && html`
        <p class="sub" data-testid="fixture-complete">
          Every frame is in. This one is played.
        </p>`}

      <div class="ways">
        <button class=${tab === 'home' ? '' : 'secondary'}
                data-testid="fixture-tab-home"
                onClick=${() => setTab('home')}>Match home</button>
        ${mayPick && html`
          <button class=${tab === 'pick' ? '' : 'secondary'}
                  data-testid="fixture-tab-pick"
                  onClick=${() => setTab('pick')}>Pick team</button>`}
      </div>

      ${tab === 'pick'
        ? html`<${PickTeam} base=${base} fixture=${fixture} />`
        : html`<${MatchHome} base=${base} fixture=${fixture}
                             failed=${failed} setFailed=${setFailed} />`}
    </div>`;
}

/**
 * The fixture as everyone watching it sees it.
 *
 * A row per slot the card declares, whether or not anybody is in it yet —
 * that is what the declared card buys: without it, "half the frames are in"
 * and "it was a short night" look identical.
 *
 * "Not yet named" and "No player named" are deliberately different: the
 * first means the sheet is not in yet, the second means it is and that slot
 * is empty — a team only leaves a slot empty when it has genuinely run out
 * of eligible players (`leaguesheet.fillable`). Neither team can default it;
 * the officer can, with a walkover if one side is empty or a write-off if
 * both are (see the walkover/write-off controls below).
 *
 * No player pickers here any more. Who plays was fixed in advance by
 * whoever holds `league.team.select` for that side, on the Pick team tab —
 * the only question left on this one is who won.
 */
function MatchHome({ base, fixture, failed, setFailed }) {
  // Which frame's pad is open, or null for the plain slot list. A frame_no
  // rather than a boolean, so leaving one frame's pad open and looking at
  // another slot is not a state this screen can even represent.
  const [padFrame, setPadFrame] = useState(null);

  const won = async (slot, winner) => {
    setFailed('');
    try {
      await post(`${base}/frames`, { frame_no: slot.frame_no, winner });
    } catch (e) { setFailed(e.detail || e.message); }
  };

  const undo = async (frameNo) => {
    setFailed('');
    try {
      await del(`${base}/frames/${frameNo}`);
    } catch (e) { setFailed(e.detail || e.message); }
  };

  // Officer-only, both of them — declaring or discarding a frame nobody
  // played is what `fixture.may_undo` already gates the Undo button on
  // (`league.fixture.write`), so it is reused here rather than adding a
  // second flag that would mean the same thing. Neither call sends a
  // body: the walkover/write-off endpoints take nothing but the frame in
  // the path, the server works out which side (if either) was absent from
  // the confirmed sheets, and refuses with its own distinct reason for
  // every wrong case — this screen only has to offer the control and
  // show whatever comes back, the same `setFailed` path `won` and `undo`
  // already use.
  const walkover = async (frameNo) => {
    setFailed('');
    try {
      await post(`${base}/frames/${frameNo}/walkover`);
    } catch (e) { setFailed(e.detail || e.message); }
  };

  const writeOff = async (frameNo) => {
    setFailed('');
    try {
      await post(`${base}/frames/${frameNo}/writeoff`);
    } catch (e) { setFailed(e.detail || e.message); }
  };

  // null: that side's sheet for this group is not confirmed at all —
  // "Not yet named". [] or a list: it is confirmed, and this slot's share
  // of it — possibly empty, possibly not, "No player named" either way if
  // empty.
  const named = (side, slot) => {
    const sheet = (fixture.sheets[side] || {})[slot.group];
    if (!sheet) return null;
    return sheet[String(slot.frame_no)] || [];
  };

  // The pad takes the whole tab over while it is open — the same "one screen
  // at a time" shape live.js uses for a casual match — rather than being
  // inlined into the slot it was opened from. A slot scored elsewhere in the
  // meantime (another device, the one-tap Won button before this loaded)
  // simply has no pad to reopen: `slot` is undefined and this falls through
  // to the ordinary list below, which already knows how to show a result.
  if (padFrame !== null) {
    const slot = fixture.slots.find((s) => s.frame_no === padFrame);
    if (slot && !slot.frame) {
      return html`
        ${failed && html`<p class="error" data-testid="fixture-frame-error">
                           ${failed}
                         </p>`}
        <${FramePad} base=${base} fixture=${fixture} slot=${slot}
                     setFailed=${setFailed} onDone=${() => setPadFrame(null)} />`;
    }
  }

  return html`
    ${failed && html`<p class="error" data-testid="fixture-frame-error">
                       ${failed}
                     </p>`}
    ${fixture.slots.map((slot) => {
      const home = named('home', slot);
      const away = named('away', slot);
      const ready = home && away && home.length > 0 && away.length > 0;
      // Both sheets in, and at least one of them left this slot empty —
      // the one shape a manager cannot resolve and only the officer can,
      // with a walkover (one side) or a write-off (both). `home`/`away`
      // are `null` until confirmed, so this stays false while either
      // sheet is still outstanding — the server's own precondition for
      // judging absence at all (`_absent_sides`) — and it is the exact
      // complement of `ready` once both are in: never both true at once.
      const shortHanded = home !== null && away !== null && !ready;
      // Three states, and they have to look different: no shots and no
      // result is "next up" (nothing here); shots and no result is the
      // live band below; a result is the block above and never this.
      const shots = (fixture.shots || {})[String(slot.frame_no)] || [];
      const frame = (!slot.frame && shots.length > 0)
        ? replay(slot, shots) : null;
      // Optional, and never gating: a frame with nobody down to referee it
      // is scored exactly the same as one with somebody watching. Rendered
      // wherever it exists rather than only before the result, so a frame
      // already played still says who was on it.
      const referee = (fixture.referees || {})[String(slot.frame_no)];
      return html`
        <div class="card stack" key=${slot.frame_no}
             data-testid=${`slot-${slot.frame_no}`}>
          <span class="rowbetween">
            <span class="sub">${`Frame ${slot.frame_no} · ${slot.group}`}</span>
            ${slot.frame && fixture.may_undo && html`
              <button class="secondary" style="width:auto"
                      data-testid=${`slot-undo-${slot.frame_no}`}
                      onClick=${() => undo(slot.frame_no)}>Undo</button>`}
          </span>
          ${referee && html`
            <span class="sub" data-testid=${`slot-referee-${slot.frame_no}`}>
              ${`Referee: ${referee.name}`}
            </span>`}

          ${slot.frame
            ? outcomeRow(fixture, slot)
            : html`
              <div class="rowbetween">
                <span class="sub">${label(home)}</span>
                <span class="mono sub">v</span>
                <span class="sub">${label(away)}</span>
              </div>
              ${frame && html`
                <div class="rowbetween"
                     data-testid=${`live-band-${slot.frame_no}`}>
                  <span class="pts">
                    ${frame.over
                      ? 'Frame over — awaiting result'
                      : `${sideName(fixture, frame.turn)} at the table`}
                  </span>
                  <span class="mono sub">
                    ${sideLine(fixture, frame, 'home')}
                    · ${sideLine(fixture, frame, 'away')}
                  </span>
                </div>`}
              ${shortHanded && fixture.may_undo && html`
                <div class="ways">
                  <button class="secondary"
                          data-testid=${`slot-walkover-${slot.frame_no}`}
                          onClick=${() => walkover(slot.frame_no)}>
                    Declare a walkover
                  </button>
                  <button class="secondary"
                          data-testid=${`slot-writeoff-${slot.frame_no}`}
                          onClick=${() => writeOff(slot.frame_no)}>
                    Write off this frame
                  </button>
                </div>`}
              ${ready && fixture.may_score && html`
                <div class="ways">
                  <button data-testid=${`slot-won-home-${slot.frame_no}`}
                          onClick=${() => won(slot, 'home')}>
                    ${fixture.home.name} won
                  </button>
                  <button data-testid=${`slot-won-away-${slot.frame_no}`}
                          onClick=${() => won(slot, 'away')}>
                    ${fixture.away.name} won
                  </button>
                </div>
                ${/* The one-tap Won buttons above are still here and still
                      work — this is a second way in, for whoever wants the
                      shot log and the referee box, not a replacement. */''}
                <button class="secondary" data-testid=${`slot-pad-${slot.frame_no}`}
                        onClick=${() => setPadFrame(slot.frame_no)}>
                  Score it shot by shot
                </button>`}`}
        </div>`;
    })}`;
}

/** What a slot says about one side when nobody has played it yet. */
function label(named) {
  if (named === null) return 'Not yet named';
  if (!named.length) return 'No player named';
  return named.map((p) => p.name).join(' & ');
}

/**
 * The result row for a played frame — three outcomes, and they must not
 * look alike. A walkover and a write-off both leave `home_players` and
 * `away_players` empty (nobody stood at the table for either), so falling
 * through to the ordinary players-row rendering for them draws `"" v ""`:
 * a blank line accounting for a real change to the fixture score, with
 * nothing on screen to explain it. `outcome` is what tells them apart —
 * it is on every frame (`projection.py` defaults missing ones to
 * `"played"` for events written before the field existed).
 */
function outcomeRow(fixture, slot) {
  const frame = slot.frame;
  if (frame.outcome === 'walkover') {
    return html`
      <div class="rowbetween" data-testid=${`slot-outcome-${slot.frame_no}`}>
        <span class="pts">${`Walkover to ${sideName(fixture, frame.winner)}`}</span>
      </div>`;
  }
  if (frame.outcome === 'writeoff') {
    return html`
      <div class="rowbetween" data-testid=${`slot-outcome-${slot.frame_no}`}>
        <span class="sub">Written off</span>
      </div>`;
  }
  return html`
    <div class="rowbetween">
      <span class=${frame.winner === 'home' ? 'pts' : 'sub'}>
        ${frame.home_players.map((p) => p.name).join(' & ')}
      </span>
      <span class="mono sub">v</span>
      <span class=${frame.winner === 'away' ? 'pts' : 'sub'}>
        ${frame.away_players.map((p) => p.name).join(' & ')}
      </span>
    </div>`;
}

/**
 * The frame's state, replayed from its shots.
 *
 * SIDES go in, not player ids — `newFrame('away', 'home')`. The side owns
 * the colour, so one reducer serves singles and doubles alike, and the
 * winner it reports is already the "home"/"away" the server records.
 *
 * Replayed on the READER's device rather than computed on the server, so
 * there is one definition of the rules and the server never learns to play
 * pool. Cheap: a frame is a few dozen shots.
 */
function replay(slot, shots) {
  const breaker = slot.breaking;
  const other = breaker === 'home' ? 'away' : 'home';
  return (shots || []).reduce(applyShot, newFrame(breaker, other));
}

/** A side's Team name, from the "home"/"away" the replayed frame deals in. */
function sideName(fixture, side) {
  return side === 'home' ? fixture.home.name : fixture.away.name;
}

/** What a side has on, and what is left of it — undecided until the table
 * stops being open, which `frame.groups` says by simply having no entry. */
function sideLine(fixture, frame, side) {
  const colour = frame.groups[side];
  if (!colour) return `${sideName(fixture, side)}: colour not yet on`;
  return `${sideName(fixture, side)}: ${colour}, ${frame.remaining[colour]} left`;
}

// A foul or the break itself can put balls down without the shot being a
// pot, and the pad has to ask what — the same shape live.js's own pad uses,
// copied here rather than imported because live.js does not export it.
const POTTABLE = new Set(['pot', 'foul', 'whitedown']);
const emptyPot = () => ({ [RED]: 0, [YELLOW]: 0, black: false });
const potIsEmpty = (p) => !p[RED] && !p[YELLOW] && !p.black;

/**
 * The scoring pad for one league frame — live.js's Pro Pool pad, reused for
 * a league table: pick a shot, then CONFIRM (the two-tap that keeps a
 * mis-tap on a phone held over a table from costing a visit), driven by the
 * same `shotsFor`/`applyShot` from rules.js and posting through `replay`'s
 * own contract — SIDES in, a side out as `frame.winner`.
 *
 * Shots are optional detail: each is posted with its own event_id and
 * fire-and-forget, so a dropped connection loses at worst a line of history,
 * never the result. The frame's OUTCOME is the one post this waits on, the
 * same way MatchHome's one-tap Won button already does.
 *
 * The one-tap escape lives here too, always on screen rather than behind a
 * toggle — "just tell me who won" has to be one tap away, not a mode you
 * have to find first, or a flat battery on a rushed night is exactly when it
 * is hardest to reach.
 */
function FramePad({ base, fixture, slot, setFailed, onDone }) {
  const initial = (fixture.shots || {})[String(slot.frame_no)] || [];
  const [shots, setShots] = useState(initial);
  const [pending, setPending] = useState(null);
  const [pot, setPot] = useState(emptyPot());
  const [busy, setBusy] = useState(false);
  const [refBusy, setRefBusy] = useState(false);

  const frame = replay(slot, shots);
  const shotsAvail = shotsFor(frame);
  const canConfirm = pending && !(pending === 'pot' && potIsEmpty(pot));

  const home = (fixture.sheets.home[slot.group] || {})[String(slot.frame_no)]
    || [];
  const away = (fixture.sheets.away[slot.group] || {})[String(slot.frame_no)]
    || [];

  const record = async (winner) => {
    setBusy(true); setFailed('');
    try {
      await post(`${base}/frames`, { frame_no: slot.frame_no, winner });
      onDone();
    } catch (e) { setFailed(e.detail || e.message); }
    finally { setBusy(false); }
  };

  const bump = (key) => () => setPot((p) => (
    key === 'black'
      ? { ...p, black: !p.black }
      // Capped at what is left, so a count can never describe a table that
      // cannot exist. Past the cap it wraps to zero rather than sticking —
      // the same rule live.js's own chips use.
      : { ...p, [key]: p[key] + 1 > frame.remaining[key] ? 0 : p[key] + 1 }));

  const confirmShot = () => {
    const shot = POTTABLE.has(pending) ? { id: pending, potted: pot }
                                       : { id: pending };
    // Minted where the shot is created, not inside the POST call — a retry
    // of this same POST must carry the same id, or the log's dedup on
    // event_id can never catch it.
    const eventId = crypto.randomUUID();
    const next = applyShot(frame, shot);
    setShots((s) => [...s, shot]);
    setPending(null); setPot(emptyPot());
    // Fire and forget. A shot is history, not a result — if this never lands
    // the frame still counts, so it must not block the tap or put an error
    // on screen over the table.
    const posted = post(`${base}/frames/${slot.frame_no}/shots`,
                        { shot, event_id: eventId }).catch(() => {});
    // The deciding shot is the exception: it races record() below on a
    // separate connection, and if record() lands first the server refuses
    // the shot with "that frame already has a result" — silently, since
    // this POST is fire-and-forget. Waiting for it here (failure still
    // ignored) means the shot is safely landed or safely refused before
    // record() ever gets a chance to beat it there.
    if (next.over) {
      posted.then(() => record(next.winner));
    }
  };

  const undoShot = () => {
    if (!shots.length) return;
    setShots((s) => s.slice(0, -1));
    setPending(null); setPot(emptyPot());
    post(`${base}/frames/${slot.frame_no}/shots/undo`).catch(() => {});
  };

  const refSide = slot.refereeing;
  const refPlayers = (fixture[refSide] && fixture[refSide].players) || [];
  const currentReferee = (fixture.referees || {})[String(slot.frame_no)];
  // `nominate_referee` is the refereeing side's own manager, or the venue's
  // own tablet — never the breaking side's manager and never the league
  // officer. `may_referee` asks the same question the server's gate asks
  // (`may_referee_fixture`), so a select offered to somebody it will
  // refuse never renders at all rather than teaching them to expect a 422.
  const mayReferee = !!(fixture.may_referee && fixture.may_referee[refSide]);

  const nominateReferee = async (refereeId) => {
    if (!refereeId) return;
    setRefBusy(true); setFailed('');
    try {
      await post(`${base}/frames/${slot.frame_no}/referee`,
                 { referee_id: refereeId });
    } catch (e) { setFailed(e.detail || e.message); }
    finally { setRefBusy(false); }
  };

  return html`
    <div class="stack" data-testid="frame-pad">
      <div class="rowbetween">
        <span class="sub">${`Frame ${slot.frame_no} · ${slot.group}`}</span>
        <button class="secondary" style="width:auto" data-testid="pad-back"
                onClick=${onDone}>Back</button>
      </div>

      <div class="rowbetween">
        <span class="sub">${label(home)}</span>
        <span class="mono sub">v</span>
        <span class="sub">${label(away)}</span>
      </div>

      ${/* Optional, and never gating: the referee comes from the side NOT
            breaking (leaguecard.refereeing_side), named by their own
            manager's device or the venue kiosk — never by signing in as the
            referee, who may not even have an account. Offered only to
            whoever `may_referee` says can actually name one; everybody else
            gets the read-only line below instead of a select the server
            will refuse. */''}
      ${mayReferee
        ? html`
          <div class="stack">
            <label>Referee</label>
            <select data-testid="pad-referee" disabled=${refBusy}
                    value=${(currentReferee && currentReferee.user_id) || ''}
                    onChange=${(e) => nominateReferee(e.target.value)}>
              <option value="">Nobody yet</option>
              ${refPlayers.map((p) => html`
                <option key=${p.user_id} value=${p.user_id}>${p.name}</option>`)}
            </select>
          </div>`
        : currentReferee && html`
          <p class="sub" data-testid="pad-referee-name">
            ${`Referee: ${currentReferee.name}`}
          </p>`}

      <div class="events">
        ${shotsAvail.map((s) => html`
          <button key=${s.id} class=${s.tone || ''}
                  aria-pressed=${pending === s.id}
                  data-testid=${`pad-shot-${s.id}`}
                  onClick=${() => { setPending(pending === s.id ? null : s.id);
                                    setPot(emptyPot()); }}>
            ${s.label}
          </button>`)}
      </div>

      ${POTTABLE.has(pending) && html`
        <div class="chips">
          <button class=${`red ${pot[RED] ? 'on' : ''}`} data-testid="pad-pot-red"
                  onClick=${bump(RED)}>${`RED${pot[RED] ? ` ×${pot[RED]}` : ''}`}</button>
          <button class=${`yellow ${pot[YELLOW] ? 'on' : ''}`}
                  data-testid="pad-pot-yellow" onClick=${bump(YELLOW)}>
            ${`YELLOW${pot[YELLOW] ? ` ×${pot[YELLOW]}` : ''}`}
          </button>
          <button class=${`black ${pot.black ? 'on' : ''}`}
                  data-testid="pad-pot-black" onClick=${bump('black')}>BLACK</button>
        </div>`}

      <button disabled=${!canConfirm || busy} data-testid="pad-confirm"
              onClick=${confirmShot}>Confirm</button>

      <button class="secondary" disabled=${!shots.length || busy}
              data-testid="pad-undo" onClick=${undoShot}>Undo</button>

      <div>
        <label>Event log</label>
        <div class="log">
          ${frame.history.length === 0
            ? html`<p class="empty">No shots yet</p>`
            : frame.history.slice().reverse().map((h, i) => html`
                <div class="card" key=${i} data-testid="pad-log">
                  ${h.text}
                </div>`)}
        </div>
      </div>

      ${/* The one-tap escape. Always here, not behind a mode switch — most
            nights nobody wants to log every visit, and a rushed one is
            exactly when reaching a toggle first is the wrong ask. */''}
      <div>
        <label>Just tell me who won</label>
        <div class="ways">
          <button class="secondary" disabled=${busy}
                  data-testid="pad-one-tap-home" onClick=${() => record('home')}>
            ${fixture.home.name} won
          </button>
          <button class="secondary" disabled=${busy}
                  data-testid="pad-one-tap-away" onClick=${() => record('away')}>
            ${fixture.away.name} won
          </button>
        </div>
      </div>
    </div>`;
}

/**
 * Picking a team sheet, for the side you are entitled to pick for.
 *
 * Picks live here and nowhere else until Confirm: only confirming writes an
 * event. That is also what produces the reveal the league asked for — the
 * home sheet is visible to the away manager precisely because it has been
 * committed, and theirs is not visible because it has not.
 *
 * A name greys out within its group once used, so the one-per-group rule is
 * something you can see rather than a refusal after the fact. The server
 * checks it anyway; this is the courtesy, not the enforcement.
 *
 * `singlesPlayed` is checked ahead of "waiting for home". Until every singles
 * slot has been played, NEITHER side has anything to pick for Doubles, home
 * included — that is a fact about the fixture, not about which side is
 * looking at it, so it has to win over the away-specific wait. Getting that
 * order backwards would put "waiting for home" on Doubles too, on a fixture
 * where nothing has been confirmed at all — two different explanations for
 * the same absent control, and a second `pick-waiting` element alongside the
 * one on Singles.
 *
 * `side` may offer a switch rather than assume: somebody who holds
 * `league.team.select` at both Teams (the common case for whoever set the
 * fixture up) picks for one side at a time, not both at once — a sheet is
 * confirmed per side, and a control that let the same screen edit both would
 * blur whose decision it was.
 */
function PickTeam({ base, fixture }) {
  const sides = ['home', 'away'].filter((s) => fixture.may_select[s]);
  const [side, setSide] = useState(sides[0]);
  const [picks, setPicks] = useState({});
  const [failed, setFailed] = useState('');

  const slotsOf = (group) => fixture.slots.filter((s) => s.group === group);
  // Present only once that (side, group) sheet has actually been confirmed
  // — see the fixture GET's `sheets`, which folds in only confirmed groups.
  // Absent, not empty, is how "not in yet" is told apart from "in and empty".
  const confirmed = (which, group) => (fixture.sheets[which] || {})[group];

  // A singles slot is "resolved", for the purpose of unlocking Doubles,
  // once it is either played OR was never playable at all — the same test
  // `commands.py`'s confirm_sheet gate applies server-side: a slot only
  // ever needs a result if BOTH sides named somebody for it. Both singles
  // sheets have to be CONFIRMED first, not merely present: an absent sheet
  // and a genuinely empty one both read as "nothing picked" for a frame
  // below, and conflating them would show the Doubles picker before
  // Singles was ever named on either side.
  //
  // A card with NO singles slots at all is a further edge, and it has to
  // short-circuit the same way `commands.py`'s own gate does: that gate
  // lives INSIDE `for slot in LS.slots_of(fixture["slots"], LC.SINGLES)`,
  // so an empty Singles list never enters the loop and never raises —
  // Doubles is confirmable immediately. Requiring both sheets to be
  // confirmed here unconditionally would disagree with that: there is no
  // Singles sheet ever to confirm, so `bothSinglesConfirmed` would stay
  // `undefined` forever and Doubles would never unlock on a doubles-only
  // card, even though the server has nothing left to wait for.
  const bothSinglesConfirmed = slotsOf('Singles').length === 0
    || (confirmed('home', 'Singles') && confirmed('away', 'Singles'));
  const singlesPlayed = bothSinglesConfirmed && slotsOf('Singles')
    .every((s) => {
      if (s.frame !== null && s.frame !== undefined) return true;
      const home = confirmed('home', 'Singles')[String(s.frame_no)] || [];
      const away = confirmed('away', 'Singles')[String(s.frame_no)] || [];
      return !(home.length && away.length);
    });

  // Keyed by side as well as group/frame: frame numbers are shared between
  // the two sides (Singles frame 1 exists for both home and away), so a
  // manager who can pick for both — see the switch below — must not have an
  // away pick overwrite an unconfirmed home pick living under the same
  // group/frame key.
  const chosen = (group, frameNo) => picks[`${side}-${group}-${frameNo}`] || [];

  const usedIn = (group, exceptFrame) => new Set(
    slotsOf(group)
      .filter((s) => s.frame_no !== exceptFrame)
      .flatMap((s) => chosen(group, s.frame_no)));

  const confirm = async (group) => {
    setFailed('');
    const body = {};
    slotsOf(group).forEach((s) => {
      body[String(s.frame_no)] = chosen(group, s.frame_no);
    });
    try {
      await post(`${base}/sheets`, { side, group, picks: body });
    } catch (e) { setFailed(e.detail || e.message); }
  };

  // A group with no slots at all has nothing to pick and nothing for the
  // server to confirm — `leaguesheet.problem` refuses it outright, so a
  // live Confirm button here would always fail. `section` is what decides
  // whether the group appears at all; `group` (below) renders its body once
  // `section` has already established there is something to render, so it
  // never has to re-check emptiness itself.
  const section = (name) => {
    if (slotsOf(name).length === 0) return null;
    return html`
      <h3>${name}</h3>
      ${group(name)}`;
  };

  const group = (name) => {
    if (confirmed(side, name)) {
      return html`<p class="sub" data-testid=${`pick-done-${name}`}>
        Your ${name.toLowerCase()} sheet is confirmed. It cannot be changed.
      </p>`;
    }
    if (name === 'Doubles' && !singlesPlayed) {
      return html`<p class="sub" data-testid="pick-doubles-why">
        The doubles are picked once the singles have been played.
      </p>`;
    }
    if (side === 'away' && !confirmed('home', name)) {
      return html`<p class="sub" data-testid="pick-waiting">
        The home sheet has not been confirmed yet. You pick once it is in.
      </p>`;
    }

    const team = fixture[side];
    // Only meaningful for away, and only once home has actually confirmed
    // this group — the entire point of ordering, made visible while away
    // is still picking rather than only afterwards on Match home.
    const homeSheet = side === 'away' ? confirmed('home', name) : null;

    return html`
      ${slotsOf(name).map((slot) => {
        const used = usedIn(name, slot.frame_no);
        const free = team.players.filter((p) => !used.has(p.user_id));
        const homeNamed = homeSheet
          ? (homeSheet[String(slot.frame_no)] || []) : null;
        return html`
          <div class="card stack" key=${slot.frame_no}
               data-testid=${`slot-${slot.frame_no}`}>
            <span class="sub">${`Frame ${slot.frame_no}`}</span>
            ${homeNamed && html`
              <span class="sub"
                    data-testid=${`pick-home-order-${slot.frame_no}`}>
                ${`${fixture.home.name}: `}
                ${homeNamed.length
                  ? homeNamed.map((p) => p.name).join(' & ')
                  : 'Nobody named'}
              </span>`}
            <${Side} slot=${slot} players=${free}
                     value=${chosen(name, slot.frame_no)}
                     testid=${`pick-${side}-${slot.frame_no}`}
                     onPick=${(v) => setPicks({
                       ...picks, [`${side}-${name}-${slot.frame_no}`]: v,
                     })} />
          </div>`;
      })}
      <button data-testid=${`pick-confirm-${name}`}
              onClick=${() => confirm(name)}>
        Confirm the ${name.toLowerCase()} — this cannot be undone
      </button>`;
  };

  return html`
    <div class="stack" data-testid=${`pick-side-${side}`}>
      ${sides.length > 1 && html`
        <div class="ways">
          ${sides.map((s) => html`
            <button key=${s} class=${side === s ? '' : 'secondary'}
                    data-testid=${`pick-switch-${s}`}
                    onClick=${() => setSide(s)}>${fixture[s].name}</button>`)}
        </div>`}
      ${failed && html`
        <p class="error" data-testid="pick-failed">${failed}</p>`}
      ${section('Singles')}
      ${section('Doubles')}
    </div>`;
}

/**
 * Who is playing, one side of one frame.
 *
 * A multiple select when the slot wants two, a plain one when it wants one —
 * the control cannot express a side the server would refuse. `players` is
 * whichever list the caller wants offered: `PickTeam` passes the roster with
 * whoever the group has already used filtered out, so a name greys out
 * within its group once picked rather than staying offered for a refusal
 * later at Confirm.
 */
function Side({ slot, players, value, onPick, testid }) {
  const many = slot.players_per_side > 1;
  return html`
    <select class="combo" multiple=${many} size=${many ? 3 : undefined}
            data-testid=${testid}
            value=${many ? value : (value[0] || '')}
            onChange=${(e) => onPick(many
              ? [...e.target.selectedOptions].map((o) => o.value)
              : [e.target.value].filter(Boolean))}>
      ${!many && html`<option value="">Nobody</option>`}
      ${players.map((p) => html`
        <option key=${p.user_id} value=${p.user_id}>${p.name}</option>`)}
    </select>`;
}
